import { useState, useRef } from "react";
import mammoth           from "mammoth";
import { processOCR } from "./utils/ocrProcessor";
import { useAuth }       from "./auth/AuthGate";
import { useTokens }     from "./payments/TokenProvider";
import TokenModal        from "./payments/TokenModal";
import TermsModal        from "./legal/TermsModal";
import PrivacyModal      from "./legal/PrivacyModal";
import GuideModal        from "./marketing/GuideModal";

// ─── Constants ────────────────────────────────────────────────────────────────
const GRADES = [
  { label: "2nd Grade", value: 2 }, { label: "3rd Grade", value: 3 },
  { label: "4th Grade", value: 4 }, { label: "5th Grade", value: 5 },
  { label: "6th Grade", value: 6 }, { label: "7th Grade", value: 7 },
  { label: "8th Grade", value: 8 }, { label: "9th Grade", value: 9 },
  { label: "10th Grade", value: 10 }, { label: "11th Grade", value: 11 },
  { label: "12th Grade", value: 12 },
];

const WRITING_MODES = [
  { label: "Single Paragraph", value: "paragraph" },
  { label: "5-Paragraph Essay", value: "essay" },
  { label: "Creative Narrative", value: "creative" },
  { label: "Unstructured / Free Write", value: "unstructured" },
];

const CREATIVE_RUBRIC = {
  narrative: {
    label: "Creative Narrative",
    icon: "📖",
    color: "#a78bfa",
    criteria: [
      { key: "voice_style", label: "Voice & Style", desc: "A distinct, consistent narrative voice that feels authentic and suits the tone of the piece." },
      { key: "story_structure", label: "Story Structure", desc: "A clear arc with an engaging opening, developed middle, and satisfying resolution or ending." },
      { key: "descriptive_detail", label: "Descriptive Detail", desc: "Sensory details and vivid language that show rather than tell, helping the reader picture the scene." },
      { key: "character_scene", label: "Character / Scene", desc: "Believable characters with personality, or a well-established setting that grounds the story." },
      { key: "coherence_flow", label: "Coherence & Flow", desc: "Logical progression of events with smooth transitions that keep the reader oriented." },
    ],
  },
};

const UNSTRUCTURED_RUBRIC = {
  freewrite: {
    label: "Writing Quality",
    icon: "💭",
    color: "#34d399",
    criteria: [
      { key: "clarity_ideas", label: "Clarity of Ideas", desc: "The main point or message is understandable and comes through clearly to the reader." },
      { key: "supporting_detail", label: "Supporting Detail", desc: "Enough context, examples, or explanation to support and develop the ideas expressed." },
      { key: "organization", label: "Organization", desc: "A logical flow of thoughts, even if informal — the writing doesn't feel scattered or disjointed." },
      { key: "voice_engagement", label: "Voice & Engagement", desc: "The writing feels authentic and holds the reader's attention through personality or genuine expression." },
    ],
  },
};

// Five-paragraph essay rubric — used in prompts and displayed in report
const ESSAY_RUBRIC = {
  intro: {
    label: "Introduction Paragraph",
    icon: "🎣",
    color: "#60a5fa",
    criteria: [
      { key: "hook", label: "Hook Statement", desc: "A broad, engaging opening sentence that grabs the reader's attention." },
      { key: "background", label: "Background Info", desc: "2–3 sentences of context on the topic or text being discussed." },
      { key: "thesis", label: "Thesis Statement", desc: "One clear sentence stating the main argument and listing all 3 supporting points." },
    ],
  },
  body: {
    label: "Body Paragraphs (×3)",
    icon: "🏗️",
    color: "#a78bfa",
    criteria: [
      { key: "topic_sentence", label: "Topic Sentence", desc: "Opens each paragraph; states the paragraph's single main point." },
      { key: "evidence", label: "Context & Evidence", desc: "Introduces and inserts a relevant quote or data point with MLA parenthetical citation (Author ##)." },
      { key: "analysis", label: "Analysis", desc: "Explains how the evidence proves the thesis — not just restating the quote." },
      { key: "transition", label: "Transition", desc: "A closing sentence that bridges smoothly to the next paragraph." },
    ],
  },
  conclusion: {
    label: "Conclusion Paragraph",
    icon: "🎯",
    color: "#34d399",
    criteria: [
      { key: "restate_thesis", label: "Restate Thesis", desc: "Thesis is reworded with different phrasing — never copied exactly." },
      { key: "synthesize", label: "Synthesize", desc: "Briefly ties all three body paragraphs together (not a simple list)." },
      { key: "final_thought", label: "Final Thought / So What?", desc: "A broader concluding statement with lasting impression or real-world connection." },
    ],
  },
};

const PARAGRAPH_RUBRIC = {
  paragraph: {
    label: "Paragraph Structure",
    icon: "📝",
    color: "#60a5fa",
    criteria: [
      { key: "topic_sentence", label: "Topic Sentence", desc: "The first sentence clearly states the main idea of the paragraph." },
      { key: "supporting_sentences", label: "Supporting Proof / Sentences", desc: "2+ sentences that develop, explain, or prove the topic sentence with details or evidence." },
      { key: "conclusion_sentence", label: "Conclusion / Clincher", desc: "A closing sentence that wraps up the paragraph's idea without simply repeating the topic sentence." },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// ── Grade-specific evaluation profiles ───────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function getGradeProfile(grade) {
  const profiles = {
    2: {
      coachingVoice: "Use very simple, cheerful language a 7-year-old can understand. Give lots of encouragement. Focus ONLY on 1 thing to celebrate and 1 simple thing to try next time.",
      feedbackInstructions: "coachFeedback must be 2–3 SHORT sentences maximum. Use words like 'great job!', 'nice work!', 'next time try...'. No complex vocabulary.",
    },
    3: {
      coachingVoice: "Use simple, warm language a 8-year-old can understand. Be very encouraging. Focus on 1–2 concrete things to celebrate and 1 simple improvement.",
      feedbackInstructions: "coachFeedback must be 3–4 short sentences. Celebrate effort and specific details. Keep suggestions simple and actionable.",
    },
    4: {
      coachingVoice: "Use friendly, encouraging language appropriate for a 9-year-old. Celebrate specific strengths before suggesting 1–2 improvements.",
      feedbackInstructions: "coachFeedback should be 2 short paragraphs. First paragraph: specific praise. Second paragraph: 1–2 concrete next steps.",
    },
    5: {
      coachingVoice: "Use positive, clear language for a 10-year-old. Balance praise with specific, actionable suggestions. Introduce writing vocabulary gently (e.g. 'topic sentence', 'detail', 'transition').",
      feedbackInstructions: "coachFeedback should be 2 paragraphs. Specific strength first, then 2 clear growth areas with examples from their writing.",
    },
    6: {
      coachingVoice: "Use encouraging but more substantive language for a 6th grader. Introduce formal writing concepts (thesis, evidence, analysis). Praise craft decisions and suggest specific techniques.",
      feedbackInstructions: "coachFeedback should be 3 paragraphs covering: (1) specific strengths, (2) primary growth area with example, (3) encouragement and next step.",
    },
    7: {
      coachingVoice: "Use clear, respectful language for a 7th grader. Hold them to 5-paragraph essay structure standards. Give specific, technical feedback on argument structure and evidence use.",
      feedbackInstructions: "coachFeedback should be 3 paragraphs. Be specific about what works and what doesn't. Reference specific sentences or sections from their writing.",
    },
    8: {
      coachingVoice: "Use respectful, collegiate-preparatory language for an 8th grader. Introduce MLA citation expectations. Give specific feedback on argument quality, analysis depth, and sentence variety.",
      feedbackInstructions: "coachFeedback should be 3–4 paragraphs. Be analytically specific — reference exact moments in the writing. Introduce vocabulary like 'synthesis', 'nuanced', 'argumentation'.",
    },
    9: {
      coachingVoice: "Use clear, academic language appropriate for a 9th grader. Hold writing to high school standards: clear thesis, strong evidence integration, analysis beyond summary.",
      feedbackInstructions: "coachFeedback should be 3–4 paragraphs. Focus on argument quality, evidence analysis, and writing mechanics. Be specific and challenging while remaining encouraging.",
    },
    10: {
      coachingVoice: "Use academic, precise language for a 10th grader. Expect sophisticated thesis statements, textual evidence with analysis, and consistent formal register. Push beyond surface-level responses.",
      feedbackInstructions: "coachFeedback should be 4 paragraphs. Give substantive feedback on argument sophistication, analytical depth, and stylistic choices. Name specific rhetorical moves.",
    },
    11: {
      coachingVoice: "Use collegiate-level language for an 11th grader. Expect strong rhetorical awareness, nuanced argumentation, MLA formatting, and intentional stylistic decisions. Push for synthesis beyond summary.",
      feedbackInstructions: "coachFeedback should be 4 paragraphs. Give rigorous, specific feedback on argument structure, rhetorical effectiveness, and stylistic control. Be direct about weaknesses while acknowledging strengths.",
    },
    12: {
      coachingVoice: "Use collegiate-level language for a 12th grader. Expect the highest standards: sophisticated argumentation, authoritative voice, precise diction, MLA compliance, and writing that reflects genuine intellectual engagement with the topic.",
      feedbackInstructions: "coachFeedback should be 4 paragraphs. Give comprehensive, analytically rigorous feedback. Hold writing to college-entrance standards. Be honest about gaps while recognizing genuine achievement.",
    },
  };
  return profiles[grade] || profiles[6];
}

// ── Grade-specific grammar, syntax, and punctuation expectations ─────────────
function getGradeGrammarGuide(grade) {
  const expectations = {
    2: `GRAMMAR & MECHANICS (Grade 2 — developmental standards):
• Spelling: Phonetic/invented spelling is acceptable and expected. Reward attempts at difficult words.
• Capitalization: First word of a sentence and "I" should be capitalized. Proper names a bonus.
• Punctuation: End punctuation (period, question mark) expected on most sentences. Do not penalize missing commas.
• Sentences: Simple sentences are the norm. Run-ons are common at this level — note gently but do not heavily penalize.`,

    3: `GRAMMAR & MECHANICS (Grade 3):
• Spelling: High-frequency words should be spelled correctly. Accept phonetic errors on complex words.
• Capitalization: Consistent capitalization of sentence beginnings, "I", and proper nouns expected.
• Punctuation: Periods and question marks consistently used. Exclamation points appropriate. Commas in lists a plus.
• Sentences: Simple and beginning compound sentences. Some run-ons expected — note but don't over-penalize.`,

    4: `GRAMMAR & MECHANICS (Grade 4):
• Spelling: Most common words correct. Dictionary-level accuracy developing for grade-appropriate vocabulary.
• Capitalization: Consistent and correct for sentences, "I", proper nouns, titles.
• Punctuation: Periods, question marks, exclamation points consistent. Commas in lists and before conjunctions expected.
• Sentences: Compound sentences expected. Complex sentences emerging. Fragment and run-on awareness developing.`,

    5: `GRAMMAR & MECHANICS (Grade 5):
• Spelling: Most words correct. Errors on advanced vocabulary are acceptable; errors on common words should be noted.
• Capitalization: Fully correct capitalization expected.
• Punctuation: Commas in compound sentences and after introductory words/phrases expected. Quotation marks for dialogue.
• Sentences: Mix of simple, compound, and beginning complex sentences expected. Run-ons and fragments should be noted.
• Tense: Consistent verb tense throughout the piece expected.`,

    6: `GRAMMAR & MECHANICS (Grade 6):
• Spelling: Correct spelling expected for all but the most advanced vocabulary.
• Capitalization: Fully correct, including titles and proper adjectives.
• Punctuation: Commas in compound and complex sentences, after introductory clauses, in series. Apostrophes for possession and contractions.
• Sentences: Deliberate sentence variety expected. Run-ons and fragments should be flagged. Subject-verb agreement required.
• Tense: Consistent tense throughout. Shifts should be intentional and clearly motivated.`,

    7: `GRAMMAR & MECHANICS (Grade 7):
• Spelling: Correct throughout. Errors on grade-level vocabulary should be noted.
• Punctuation: Full command of commas, apostrophes, quotation marks, colons in lists. Semicolons introduced.
• Sentences: Intentional sentence variety — simple, compound, complex, compound-complex. No unintentional fragments or run-ons.
• Agreement: Subject-verb and pronoun-antecedent agreement required.
• Tense: Consistent and controlled. Any shifts must be purposeful.`,

    8: `GRAMMAR & MECHANICS (Grade 8):
• Spelling: Error-free spelling expected.
• Punctuation: Full command of all punctuation. Semicolons used correctly. Dashes and colons appropriate.
• Sentences: Sophisticated sentence variety demonstrating stylistic awareness. Fragments only if clearly intentional.
• Agreement: Flawless subject-verb and pronoun agreement.
• Word Choice: Precise, academic vocabulary appropriate to the task. Avoid vague words (good, bad, nice, very).
• MLA: Parenthetical citations (Author ##) required when quoting. Works Cited awareness a plus.`,

    9: `GRAMMAR & MECHANICS (Grade 9):
• All mechanics should be error-free at this level.
• Punctuation: Full command including semicolons, colons, em dashes used purposefully.
• Sentences: Varied, sophisticated sentence structures. Fragments and run-ons are errors unless clearly rhetorical.
• Diction: Precise academic vocabulary. Informal language, slang, and vague terms (things, stuff, very, really) should be noted.
• MLA: Citations required when quoting. Format should be correct.
• Register: Consistent formal register throughout unless mode calls for informal voice.`,

    10: `GRAMMAR & MECHANICS (Grade 10):
• All mechanics error-free. Any grammar or punctuation error at this level is significant.
• Sentence structures should be deliberately varied for rhetorical effect — not just grammatically correct.
• Diction must be precise and academic. Hedge words (kind of, sort of, basically) and vague language should be flagged.
• Punctuation used rhetorically — colons, semicolons, em dashes as intentional style choices, not accidents.
• MLA citations correctly formatted. Pronoun reference clear and unambiguous throughout.`,

    11: `GRAMMAR & MECHANICS (Grade 11):
• Zero tolerance for basic grammar, spelling, or punctuation errors.
• Sentence structures reflect rhetorical sophistication — parallelism, periodic sentences, anaphora where appropriate.
• Diction is precise, deliberate, and appropriate to the rhetorical context. Word choice elevates the argument.
• All punctuation purposeful and correct. Semicolons, colons, and em dashes used with confidence.
• MLA compliance required. Any citation errors are significant at this level.`,

    12: `GRAMMAR & MECHANICS (Grade 12 — college-entrance standard):
• Complete mastery of grammar and mechanics expected. Any error is notable.
• Sentence construction reflects authorial control — structure is a rhetorical tool, not just a container for ideas.
• Diction is sophisticated, precise, and consistent with the writer's voice and argument.
• Punctuation is stylistically controlled and intentional throughout.
• MLA fully compliant. Citation errors are unacceptable at this level.
• Register is consistently appropriate — formal where required, deliberately varied where purposeful.`,
  };
  return `\n${expectations[grade] || expectations[6]}`;
}

// ── Grade-specific paragraph rubric guide ─────────────────────────────────────
function getGradeParagraphGuide(grade) {
  const expectations = {
    2: `SINGLE PARAGRAPH RUBRIC for Grade 2 (apply with generous, developmental standards):
• Topic Sentence: A simple sentence telling what the paragraph is about. Accept "My favorite..." or "I like..." as valid topic sentences. Award full credit for any clear opening idea.
• Supporting Sentences: 1–2 sentences that add a detail or reason. Accept personal experience as evidence. Do not penalize for short responses.
• Conclusion Sentence: Any sentence that signals the end ("That is why...", "So..."). Accept simple closings.
Grade 2 standard: Most students write 3–5 sentences total. Evaluate relative to this expectation, not adult standards.`,

    3: `SINGLE PARAGRAPH RUBRIC for Grade 3:
• Topic Sentence: First sentence states the main idea clearly. Accept personal opinion starters ("I think...", "My favorite...").
• Supporting Sentences: 2–3 sentences with details or reasons. Personal experience counts as evidence.
• Conclusion Sentence: A closing sentence that wraps up the idea. Simple restatements are acceptable.
Grade 3 standard: 4–6 sentences total is excellent. Reward effort to include specific details.`,

    4: `SINGLE PARAGRAPH RUBRIC for Grade 4:
• Topic Sentence: Clear statement of the paragraph's main idea. Should not be a question.
• Supporting Sentences: 2–3 sentences developing the topic with details, reasons, or examples. Look for "because" connections.
• Conclusion Sentence: A closing sentence that doesn't simply copy the topic sentence. "So...", "That is why..." starters are good signs.
Grade 4 standard: 5–7 sentences. Beginning to use transition words (first, next, also, finally).`,

    5: `SINGLE PARAGRAPH RUBRIC for Grade 5:
• Topic Sentence: Clear, specific claim about the topic. Should go beyond "I think" to state a supportable point.
• Supporting Sentences: 3+ sentences with specific details, examples, or simple evidence. Look for transition words between ideas.
• Conclusion Sentence: Closes the paragraph without repeating the opener verbatim. May connect to a broader idea.
Grade 5 standard: 6–8 sentences. Transition words expected. Evidence should be more specific than Grade 4.`,

    6: `SINGLE PARAGRAPH RUBRIC for Grade 6:
• Topic Sentence: A clear, arguable claim the paragraph will prove. Not a question, not just a fact.
• Supporting Sentences: 3+ sentences with specific evidence, examples, or reasoning. At least one piece of concrete support expected.
• Conclusion Sentence: Synthesizes the paragraph's point — connects back to the main idea without direct repetition.
Grade 6 standard: Paragraphs should feel complete and logical. Transition words and sentence variety expected.`,

    7: `SINGLE PARAGRAPH RUBRIC for Grade 7:
• Topic Sentence: Specific, arguable claim tied to a clear position. Should forecast what the paragraph proves.
• Supporting Sentences: 3–4 sentences with specific evidence. Explanation of HOW evidence supports the claim is expected (not just "this proves...").
• Conclusion Sentence: Does not simply restate the topic sentence. Should deepen or extend the idea.
Grade 7 standard: Paragraph logic should be clear and complete. Transitions should be smooth, not formulaic.`,

    8: `SINGLE PARAGRAPH RUBRIC for Grade 8:
• Topic Sentence: Precise, analytical claim. Should clearly connect to an overarching argument.
• Supporting Sentences: 3–4 sentences. Evidence should be integrated smoothly (not just dropped in). Analysis must explain HOW the evidence supports the claim, not just THAT it does.
• Conclusion Sentence: Extends thinking beyond the paragraph — connects to a larger significance.
Grade 8 standard: MLA parenthetical citations expected if quoting. Analysis should go beyond surface-level.`,

    9: `SINGLE PARAGRAPH RUBRIC for Grade 9:
• Topic Sentence: Sophisticated, arguable claim that advances a clear analytical position.
• Supporting Sentences: Well-integrated evidence with genuine analysis. The "so what?" must be answered — why does this evidence matter?
• Conclusion Sentence: Synthesizes the paragraph's contribution to the larger argument.
Grade 9 standard: Writing should demonstrate real analytical thinking, not just evidence collection. MLA citations expected.`,

    10: `SINGLE PARAGRAPH RUBRIC for Grade 10:
• Topic Sentence: Nuanced claim that contributes to a sophisticated larger argument. Avoid obvious or overly broad claims.
• Supporting Sentences: Evidence integrated with smooth attribution. Analysis must be specific and go beyond paraphrase. The warrant (logical connection) must be explicit.
• Conclusion Sentence: Completes the analytical arc and points toward larger implications.
Grade 10 standard: Paragraph should demonstrate analytical sophistication. Sentence variety and precise diction expected.`,

    11: `SINGLE PARAGRAPH RUBRIC for Grade 11:
• Topic Sentence: Precise, arguable, and nuanced — no hedging, no obvious claims.
• Supporting Sentences: Sophisticated evidence integration. Analysis must be original, not just restating what the evidence says. Multiple pieces of evidence synthesized where appropriate.
• Conclusion Sentence: Adds analytical value — doesn't just summarize. Connects to theme, context, or broader significance.
Grade 11 standard: Rhetorical control and analytical rigor expected. MLA compliance required.`,

    12: `SINGLE PARAGRAPH RUBRIC for Grade 12:
• Topic Sentence: Authoritative, precise claim reflecting genuine intellectual engagement with the topic.
• Supporting Sentences: Masterful evidence integration with rigorous, original analysis. Counterarguments or complications acknowledged where appropriate.
• Conclusion Sentence: Contributes meaningfully to the larger argument — adds insight rather than just closing the paragraph.
Grade 12 standard: College-entrance level writing. Evaluate for intellectual sophistication, not just technical compliance.`,
  };
  return `\n${expectations[grade] || expectations[6]}\n${getGradeGrammarGuide(grade)}`;
}

// ── Grade-specific essay rubric guide ────────────────────────────────────────
function getGradeEssayGuide(grade) {
  const gradeNote = grade <= 5
    ? `Grade ${grade} Essay Note: Students at this level are LEARNING essay structure. Apply standards developmentally — reward clear attempts at each component, even if imperfectly executed. Do not penalize for missing MLA citations.`
    : grade <= 7
      ? `Grade ${grade} Essay Note: Students should demonstrate clear 5-paragraph structure. MLA citations not yet required but direct quotes should be acknowledged. Hold to standard but reward genuine effort.`
      : grade <= 9
        ? `Grade ${grade} Essay Note: Hold to full 5-paragraph standards. MLA parenthetical citations (Author ##) expected when quoting. Analysis must go beyond summary.`
        : `Grade ${grade} Essay Note: College-preparatory standards apply. Full MLA compliance required. Analysis must be sophisticated and original. Counterargument awareness expected in strong essays.`;

  const mlaNote = grade >= 8
    ? `• Context & Evidence: Quote or specific data MUST include MLA parenthetical citation (Author ##). Deduct significantly for missing citations at this grade level.`
    : grade >= 6
      ? `• Context & Evidence: Quote or specific fact to support the point. MLA citations are a bonus at this level, not required.`
      : `• Context & Evidence: A specific detail, fact, or example that supports the topic sentence. Personal experience is acceptable.`;

  const analysisNote = grade >= 8
    ? `• Analysis: Must explain HOW and WHY the evidence proves the thesis — not just what it shows. Surface-level "this shows that..." is insufficient at this grade.`
    : grade >= 6
      ? `• Analysis: A sentence explaining how the evidence connects to the topic sentence. "This shows..." is acceptable but reward more sophisticated connections.`
      : `• Analysis: A sentence in the student's own words explaining why the detail matters. Accept simple "because..." connections.`;

  return `
FIVE-PARAGRAPH ESSAY RUBRIC (apply strictly):
${gradeNote}

PARAGRAPH 1 — Introduction:
• Hook: An opening sentence that grabs the reader's attention. ${grade <= 4 ? "Accept any interesting opener — a question, a fun fact, or a bold statement." : grade <= 7 ? "Should be broad and engaging — a question, anecdote, or surprising fact." : "Must be sophisticated and genuinely engaging — avoid clichés like 'Since the dawn of time...'"}
• Background Info: ${grade <= 4 ? "1–2 sentences about the topic." : grade <= 7 ? "2–3 sentences giving context on the topic." : "2–3 sentences of substantive context that sets up the argument — not just definitions."}
• Thesis Statement: ${grade <= 4 ? "One sentence saying what the essay is about and listing the main points." : grade <= 7 ? "One sentence stating the argument and listing all 3 supporting points." : "One precise, arguable sentence stating the claim AND previewing all 3 supporting points. Not a statement of fact."}

PARAGRAPHS 2, 3, 4 — Body Paragraphs (evaluate each, report average):
• Topic Sentence: ${grade <= 5 ? "A sentence stating what this paragraph is about." : "Opens the paragraph; states the paragraph's single main point tied to the thesis."}
${mlaNote}
${analysisNote}
• Transition: ${grade <= 4 ? "Any sentence that moves to the next idea." : grade <= 6 ? "A closing sentence that signals the next point." : "A closing sentence that bridges smoothly to the next paragraph's specific argument."}

PARAGRAPH 5 — Conclusion:
• Restate Thesis: ${grade <= 5 ? "A sentence that reminds the reader of the main idea using different words." : "Thesis restated with noticeably different phrasing — penalize if copied verbatim."}
• Synthesize: ${grade <= 5 ? "A sentence mentioning each of the main points." : grade <= 8 ? "Briefly ties all three body paragraph arguments together (not just a list)." : "Synthesizes the three arguments — shows how they work together, not just a summary list."}
• Final Thought: ${grade <= 5 ? "A sentence that says this is the end ('That is why...', 'In conclusion...' is fine)." : grade <= 8 ? "A broader concluding statement with a real-world connection or lasting impression." : "A sophisticated closing that adds genuine insight — avoids clichés and leaves a meaningful impression."}\n${getGradeGrammarGuide(grade)}`;
}

// ── Grade-specific creative rubric guide ────────────────────────────────────
function getGradeCreativeGuide(grade) {
  const literarySection = grade >= 6 ? `
LITERARY DEVICES (expected at Grade ${grade}):
• Metaphor & Imagery: ${grade <= 7 ? "Simple comparisons (similes, basic metaphors) and sensory details." : grade <= 9 ? "Deliberate figurative language — metaphors, personification, imagery that serves the story." : "Sophisticated, purposeful figurative language integrated naturally into the narrative."}
• Dialogue & Pacing: ${grade <= 7 ? "Any attempt at dialogue. Basic scene pacing." : grade <= 9 ? "Natural dialogue that reveals character. Intentional control of pace." : "Masterful dialogue and precise pacing — uses scene and summary deliberately."}
• Foreshadowing & Tension: ${grade <= 7 ? "Any hint at what might happen next. Basic story tension." : grade <= 9 ? "Deliberate hints or tension-building that pays off in the story." : "Sophisticated foreshadowing and sustained narrative tension."}${grade >= 8 ? `\n• Symbolism & Theme: ${grade <= 9 ? "Emerging thematic awareness — is there a clear message or deeper meaning?" : "Deliberate symbolism and developed theme — the story means something beyond its surface events."}` : ""}` : "";

  return `
CREATIVE NARRATIVE RUBRIC (do NOT penalize for absence of essay structure):
Grade ${grade} creative expectations:

• Voice & Style: ${grade <= 3 ? "Does the writing sound like the student? Any personal touches count." : grade <= 5 ? "A consistent narrative voice. The student's personality should come through." : grade <= 7 ? "A distinct, consistent voice appropriate to the story's tone. Word choice reflects intentionality." : grade <= 9 ? "A controlled, purposeful narrative voice with consistent register and tone." : "A sophisticated, rhetorically aware voice — tone, register, and style serve the story's purpose."}

• Story Structure: ${grade <= 3 ? "A beginning and some kind of middle. Any sense of sequence counts." : grade <= 5 ? "Clear beginning, middle, end. Events connect logically." : grade <= 7 ? "A complete story arc — engaging opening, developed middle with rising action, satisfying conclusion." : grade <= 9 ? "A deliberate narrative arc with exposition, conflict, rising action, climax, and resolution." : "A masterfully constructed arc — every scene serves the narrative. Structure is deliberate and purposeful."}

• Descriptive Detail: ${grade <= 3 ? "Any detail beyond the bare minimum. Even one vivid word is worth celebrating." : grade <= 5 ? "Specific details that help the reader picture the story. Some sensory language." : grade <= 7 ? "Sensory details that create vivid scenes. Show-don't-tell beginning to emerge." : grade <= 9 ? "Consistent show-don't-tell. Specific, purposeful details that build atmosphere." : "Masterful use of concrete, specific detail. Every descriptive choice is deliberate and effective."}

• Character / Scene: ${grade <= 3 ? "A character is named and does something. Any setting detail." : grade <= 5 ? "Characters have some personality. Setting is mentioned." : grade <= 7 ? "Characters feel real with personality and motivation. Setting is established." : grade <= 9 ? "Rounded characters with clear motivation. Setting contributes to mood or theme." : "Complex characters whose actions reveal personality. Setting and character are meaningfully integrated."}

• Coherence & Flow: ${grade <= 3 ? "The writing makes sense and events are in order." : grade <= 5 ? "Events connect logically. Basic transitions present." : grade <= 7 ? "Smooth transitions. The narrative progresses naturally." : grade <= 9 ? "Controlled pacing and flow. Transitions serve the story's rhythm." : "Seamless flow — the reader is never confused or pulled out of the story."}
${literarySection}\n${getGradeGrammarGuide(grade)}`;
}

// ── Grade-specific unstructured rubric guide ─────────────────────────────────
function getGradeUnstructuredGuide(grade) {
  return `
UNSTRUCTURED / FREE WRITE RUBRIC (do NOT penalize for lack of formal structure):
Grade ${grade} expectations for free-form writing:

• Clarity of Ideas: ${grade <= 4 ? "Can the reader understand the main point? Accept any clear central idea, however simply expressed." : grade <= 7 ? "Is the main point or message clear and understandable? Ideas should follow a logical sequence." : "Is the central argument or idea clear and precisely communicated? Vagueness should be noted."}

• Supporting Detail: ${grade <= 4 ? "Does the student add at least one detail or reason? Any support counts." : grade <= 7 ? "Does the writer include enough details, examples, or context to make the point convincing?" : grade <= 10 ? "Does the writer include specific, compelling evidence or examples? Generic statements should be noted." : "Is the supporting detail specific, original, and well-integrated? Clichés and vague generalizations should be flagged."}

• Organization: ${grade <= 4 ? "Is there any sense of order? Beginning → middle → end, or any logical sequence." : grade <= 7 ? "Is there a logical flow of thoughts, even if informal? Ideas should connect." : grade <= 10 ? "Does the writing have a clear organizational logic that guides the reader, even without formal structure?" : "Is the organizational strategy deliberate and effective? The reader should never feel lost."}

• Voice & Engagement: ${grade <= 4 ? "Does the writing feel like it comes from a real person? Any authentic expression counts." : grade <= 7 ? "Does the writing feel authentic and hold the reader's attention?" : grade <= 10 ? "Is there a distinct, genuine voice? Does the writing engage the reader throughout?" : "Is the voice sophisticated, controlled, and genuinely compelling? The best free writing has a strong rhetorical presence."}\n${getGradeGrammarGuide(grade)}`;
}


async function fileToBase64(file) {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const chunks = [];
  for (let i = 0; i < bytes.length; i += 8192) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + 8192)));
  }
  return btoa(chunks.join(""));
}

// Retries up to 3x with 600ms delay — handles Windows antivirus brief locks
async function fileToBase64WithRetry(file, retries = 3, delayMs = 600) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fileToBase64(file);
    } catch (err) {
      if (attempt === retries) throw err;
      await new Promise(r => setTimeout(r, delayMs));
    }
  }
}

function getMediaType(file) {
  if (file.type === "application/pdf") return "application/pdf";
  if (file.type.startsWith("image/")) return file.type;
  if (file.type === "text/plain" || file.name.endsWith(".txt")) return "text/plain";
  if (
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.name.endsWith(".docx")
  ) return "docx";
  return null;
}

function cleanDocxText(raw) {
  return raw
    .replace(/\r\n/g, "\n")           // normalize line endings
    .replace(/\n{3,}/g, "\n\n")       // collapse 3+ blank lines → one
    .replace(/^[ \t]*[•·▪▸►‣⁃◦▹]\s*/gm, "") // strip bullet symbols
    .replace(/^\t+/gm, "")            // strip tab indentation
    .replace(/\uFFFD/g, "")           // strip replacement characters
    .trim();
}

function countParagraphs(text) {
  return text.split(/\n\s*\n/).filter(p => p.trim().length > 20).length;
}

const WORD_LIMIT                  = 1200;
const PDF_PAGE_CAP                = 3;
const WORD_THRESHOLD_2_TOKEN      = 800;   // >800 words → 2 base tokens
const WORD_THRESHOLD_GRADE27_WARN = 600;   // grades 2–7, >600 words → warning
const DRAFT_CHECK_TOKEN_COST      = 1;     // cost per on-demand draft tool (grammar/selection/revision-ideas)

const footerLinkStyle = {
  background: "transparent", border: "none",
  color: "#484f58", cursor: "pointer",
  fontSize: 11, padding: 0,
  fontFamily: "'DM Sans', sans-serif",
  textDecoration: "underline",
  textUnderlineOffset: 2,
};

const footerAppLinkStyle = {
  color: "#8b949e", fontSize: 11,
  textDecoration: "none",
  fontFamily: "'DM Sans', sans-serif",
};

// Dynamic output token cap based on grade and word count
function getMaxOutputTokens(grade, wordCount) {
  if (grade <= 4) return 1500;
  if (grade <= 7) return 2000;
  // Grade 8–12
  if (wordCount > WORD_THRESHOLD_2_TOKEN) return 3500;
  return 2500;
}

// Base eval tokens from word count (OCR surcharge added separately)
function calcBaseEvalTokens(wordCount) {
  return wordCount > WORD_THRESHOLD_2_TOKEN ? 2 : 1;
}

// Helper: is the upload a raw image (not PDF/txt/docx)?
function isImageUpload(mediaType) {
  return mediaType && mediaType.startsWith("image/");
}

function countWords(text) {
  return text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
}

function ratingColor(r) {
  if (r >= 4) return "#4ade80";
  if (r >= 3) return "#facc15";
  if (r >= 2) return "#fb923c";
  return "#f87171";
}

function ratingLabel(r) {
  if (r >= 4.5) return "Excellent";
  if (r >= 3.5) return "Strong";
  if (r >= 2.5) return "Developing";
  if (r >= 1.5) return "Emerging";
  return "Needs Work";
}

// ─── Sub-components ───────────────────────────────────────────────────────────

export function RatingBar({ label, value, desc, max = 5 }) {
  const pct = Math.round((value / max) * 100);
  const col = ratingColor(value);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontSize: 13, color: "#c9d1d9", fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: col }}>{value.toFixed(1)} — {ratingLabel(value)}</span>
      </div>
      {desc && <div style={{ fontSize: 11, color: "#484f58", marginBottom: 5 }}>{desc}</div>}
      <div style={{ height: 7, background: "#21262d", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: col, borderRadius: 99, transition: "width 1s ease" }} />
      </div>
    </div>
  );
}

export function RubricSection({ section, ratings, feedback }) {
  return (
    <div style={{
      border: `1px solid ${section.color}33`,
      borderRadius: 10,
      marginBottom: 16,
      overflow: "hidden",
    }}>
      <div style={{
        background: `${section.color}14`,
        padding: "10px 16px",
        borderBottom: `1px solid ${section.color}33`,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>{section.icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: section.color, letterSpacing: 0.3 }}>{section.label}</span>
      </div>
      <div style={{ padding: "16px" }}>
        {section.criteria.map(c => {
          const val = ratings?.[c.key] ?? 0;
          const fb = feedback?.[c.key] ?? "";
          return (
            <div key={c.key} style={{ marginBottom: 14 }}>
              <RatingBar label={c.label} value={val} desc={c.desc} />
              {fb && (
                <div style={{
                  fontSize: 12, color: "#8b949e", marginTop: 4,
                  padding: "6px 10px", background: "#0d1117",
                  borderRadius: 6, borderLeft: `2px solid ${section.color}55`,
                  lineHeight: 1.5,
                }}>{fb}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AnnotatedText({ annotated }) {
  const [activeIdx, setActiveIdx] = useState(null);
  if (!annotated || !annotated.segments) return null;
  const colors = {
    strength: { text: "#4ade80", bg: "rgba(74,222,128,0.13)", border: "#4ade80" },
    improve: { text: "#fb923c", bg: "rgba(251,146,60,0.13)", border: "#fb923c" },
    structure: { text: "#60a5fa", bg: "rgba(96,165,250,0.13)", border: "#60a5fa" },
    style: { text: "#c084fc", bg: "rgba(192,132,252,0.13)", border: "#c084fc" },
  };
  return (
    <div>
      <div style={{ lineHeight: 2, fontFamily: "'DM Serif Display', serif", fontSize: 16, color: "#e6edf3" }}>
        {annotated.segments.map((seg, i) => {
          if (seg.type === "original") return <span key={i}>{seg.text}</span>;
          const c = colors[seg.annotationType] || colors.improve;
          const isActive = activeIdx === i;
          return (
            <span key={i} style={{ position: "relative", display: "inline" }}>
              <span
                onClick={() => setActiveIdx(isActive ? null : i)}
                style={{
                  background: c.bg,
                  color: c.text,
                  borderBottom: `2px solid ${c.border}`,
                  paddingBottom: 1,
                  cursor: "pointer",
                  borderRadius: 3,
                  transition: "background 0.15s",
                }}
              >{seg.text}</span>
              {isActive && seg.comment && (
                <span style={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)",
                  left: 0,
                  background: "#1c2128",
                  border: `1px solid ${c.border}`,
                  color: c.text,
                  fontSize: 12,
                  padding: "8px 12px",
                  borderRadius: 8,
                  whiteSpace: "pre-wrap",
                  maxWidth: 280,
                  zIndex: 50,
                  boxShadow: "0 8px 28px rgba(0,0,0,0.6)",
                  lineHeight: 1.6,
                  fontFamily: "'DM Sans', sans-serif",
                  display: "block",
                }}>
                  <strong style={{ display: "block", marginBottom: 3, textTransform: "uppercase", fontSize: 10, letterSpacing: 1 }}>
                    {seg.annotationType}
                  </strong>
                  {seg.comment}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function DownloadReport({ result, grade, mode, text }) {
  const handleDownload = () => {
    const g = GRADES.find(g => g.value === grade)?.label || `Grade ${grade}`;
    const m = WRITING_MODES.find(m => m.value === mode)?.label || mode;
    const now = new Date().toLocaleDateString();

    const rubric = mode === "essay" ? ESSAY_RUBRIC : PARAGRAPH_RUBRIC;
    const allSections = Object.values(rubric);

    let rubricLines = "";
    allSections.forEach(sec => {
      rubricLines += `\n${sec.icon} ${sec.label.toUpperCase()}\n${"─".repeat(40)}\n`;
      sec.criteria.forEach(c => {
        const val = result.criteriaRatings?.[c.key] ?? 0;
        const fb = result.criteriaFeedback?.[c.key] ?? "";
        rubricLines += `  ${c.label}: ${val.toFixed(1)}/5.0 — ${ratingLabel(val)}\n`;
        if (fb) rubricLines += `  → ${fb}\n`;
      });
    });

    const styleLines = result.styleRatings
      ? Object.entries(result.styleRatings).map(([k, v]) =>
          `  ${k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}: ${v.toFixed(1)}/5.0 — ${ratingLabel(v)}`
        ).join("\n")
      : "";

    const content = `
WRITING COACH — EVALUATION REPORT
====================================
Date: ${now}
Grade: ${g}  |  Format: ${m}
Overall Score: ${result.overallScore?.toFixed(1) || "—"} / 5.0 — ${ratingLabel(result.overallScore || 0)}

════════════════════════════════════
WHAT YOUR WRITING COMMUNICATES
════════════════════════════════════
${result.writingAbout || ""}

════════════════════════════════════
COACH FEEDBACK
════════════════════════════════════
${result.coachFeedback || ""}

════════════════════════════════════
STRENGTHS
════════════════════════════════════
${(result.strengths || []).map(s => "  ✓ " + s).join("\n")}

════════════════════════════════════
OPPORTUNITIES TO GROW
════════════════════════════════════
${(result.opportunities || []).map(o => "  → " + o).join("\n")}

════════════════════════════════════
STRUCTURE RUBRIC RATINGS
════════════════════════════════════
${rubricLines}
${styleLines ? `\n════════════════════════════════════\nSTYLE & MECHANICS (Grades 6–12)\n════════════════════════════════════\n${styleLines}` : ""}

════════════════════════════════════
WRITING SUBMITTED
════════════════════════════════════
${text}
    `.trim();

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `writing-coach-${now.replace(/\//g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button onClick={handleDownload} style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      background: "linear-gradient(135deg, #1f6feb, #388bfd)",
      color: "#fff", border: "none", borderRadius: 8,
      padding: "10px 20px", cursor: "pointer",
      fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 14,
    }}>
      ⬇ Download Full Report
    </button>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function WritingCoach() {
  const { getToken, signOut } = useAuth();
  const { tokenBalance, refreshBalance } = useTokens();
  const [showTokenModal,   setShowTokenModal]   = useState(false);
  const [pdfTruncated,     setPdfTruncated]     = useState(null);
  const [wordCountWarning, setWordCountWarning] = useState(null);
  const [showFooterTerms,  setShowFooterTerms]  = useState(false);
  const [showFooterPrivacy, setShowFooterPrivacy] = useState(false);
  const [showGuide,        setShowGuide]        = useState(false);

  const [grade, setGrade] = useState(6);
  const [inputMode, setInputMode] = useState("type");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [fileMediaType, setFileMediaType] = useState(null);
  const [fileText, setFileText] = useState(null);       // pre-extracted text for txt/docx
  const [isDragging, setIsDragging] = useState(false);  // drag-over state
  const [hasDocxArtifacts, setHasDocxArtifacts] = useState(false); // Word formatting flag
  const [hasExcludedTypedText, setHasExcludedTypedText] = useState(false); // handwriting OCR flag
  const [excludedTextNote, setExcludedTextNote] = useState(""); // description of what was excluded
  const [detectedMode, setDetectedMode] = useState(null);
  const [confirmedMode, setConfirmedMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [result, setResult] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [promptResult, setPromptResult] = useState(null);
  const [tab, setTab] = useState("feedback");
  const [error, setError] = useState("");
  const fileInputRef = useRef();

  // ─── Draft-workspace tools (on-demand grammar/sentence/revision help) ──────
  const [draftChecks, setDraftChecks] = useState([]);       // history: {id, type, label, content}
  const [draftChecking, setDraftChecking] = useState(null); // which tool key is in flight
  const [draftCheckError, setDraftCheckError] = useState("");
  const [selectionRange, setSelectionRange] = useState(null); // {start, end, text} | null

  // ─── Teacher-provided rubric (optional, +1 token) ──────────────────────────
  const [showCustomRubric, setShowCustomRubric] = useState(false);
  const [rubricInputMode, setRubricInputMode] = useState("type"); // "type" | "upload"
  const [rubricText, setRubricText] = useState("");
  const [rubricFile, setRubricFile] = useState(null);
  const [rubricFilePreview, setRubricFilePreview] = useState(null);
  const [rubricFileBase64, setRubricFileBase64] = useState(null);
  const [rubricFileMediaType, setRubricFileMediaType] = useState(null);
  const [rubricFileText, setRubricFileText] = useState(null);
  const [rubricIsDragging, setRubricIsDragging] = useState(false);
  const [rubricError, setRubricError] = useState("");
  const [rubricPdfTruncated, setRubricPdfTruncated] = useState(null);
  const rubricFileInputRef = useRef();

  // Pure extractor (no state side effects) — shared by the main submission
  // upload and the teacher-rubric upload.
  async function extractFileContents(f, mt) {
    if (mt === "text/plain") {
      try {
        const fileText = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = () => reject(new Error("Failed to read text file."));
          reader.readAsText(f);
        });
        return { fileText };
      } catch (err) {
        return { error: err.message };
      }

    } else if (mt === "docx") {
      try {
        const arrayBuffer = await f.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        const cleaned = cleanDocxText(result.value);
        if (!cleaned) return { error: "Could not extract text from this Word document. Try saving as PDF and uploading that instead." };
        return { fileText: cleaned, hasDocxArtifacts: !!(result.messages && result.messages.length > 0) };
      } catch (err) {
        return { error: "Failed to read Word document: " + err.message };
      }

    } else {
      // PDF or image — read immediately (before input reset) with retry for Windows file locks
      try {
        const fileBase64 = await fileToBase64WithRetry(f);
        const filePreviewUrl = f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
        return { fileBase64, filePreviewUrl };
      } catch (err) {
        return { error: "Could not read this file. Try moving it to your Desktop and uploading from there, or save a copy and try again." };
      }
    }
  }

  const processFile = async (f) => {
    setFile(f); setResult(null); setDetectedMode(null); setConfirmedMode(null);
    setFileText(null); setFileBase64(null); setFilePreview(null); setHasDocxArtifacts(false);
    setError("");

    const mt = getMediaType(f);
    if (!mt) {
      setError("Unsupported file type. Please upload a PDF, Word doc (.docx), plain text (.txt), JPG, PNG, or WEBP.");
      setFile(null);
      return;
    }
    setFileMediaType(mt);

    const r = await extractFileContents(f, mt);
    if (r.error) {
      setError(r.error);
      if (mt !== "docx") setFile(null);
      return;
    }
    if (r.fileText !== undefined) setFileText(r.fileText);
    if (r.fileBase64) setFileBase64(r.fileBase64);
    if (r.filePreviewUrl) setFilePreview(r.filePreviewUrl);
    if (r.hasDocxArtifacts) setHasDocxArtifacts(true);
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await processFile(f);
    // Reset input AFTER file is fully read so Windows doesn't invalidate the reference mid-read
    e.target.value = "";
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    // Only leave drag state when exiting the drop zone itself, not a child element
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) await processFile(f);
  };

  const processRubricFile = async (f) => {
    setRubricFile(f); setRubricFileText(null); setRubricFileBase64(null);
    setRubricFilePreview(null); setRubricError("");

    const mt = getMediaType(f);
    if (!mt) {
      setRubricError("Unsupported file type. Please upload a PDF, Word doc (.docx), plain text (.txt), JPG, PNG, or WEBP.");
      setRubricFile(null);
      return;
    }
    setRubricFileMediaType(mt);

    const r = await extractFileContents(f, mt);
    if (r.error) {
      setRubricError(r.error);
      if (mt !== "docx") setRubricFile(null);
      return;
    }
    if (r.fileText !== undefined) setRubricFileText(r.fileText);
    if (r.fileBase64) setRubricFileBase64(r.fileBase64);
    if (r.filePreviewUrl) setRubricFilePreview(r.filePreviewUrl);
  };

  const handleRubricFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await processRubricFile(f);
    e.target.value = "";
  };

  const handleRubricDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setRubricIsDragging(true); };
  const handleRubricDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); setRubricIsDragging(true); };
  const handleRubricDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setRubricIsDragging(false);
  };
  const handleRubricDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setRubricIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) await processRubricFile(f);
  };

  const callClaude = async (messages, systemPrompt, maxTokens = 2000, deductCount = 0, notes = undefined) => {
    const token = getToken();
    if (!token) throw new Error("Session expired. Please sign in again.");

    const res = await fetch("/.netlify/functions/wc-evaluate", {
      method:  "POST",
      headers: {
        "Content-Type":  "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        messages,
        system:       systemPrompt,
        max_tokens:   maxTokens,
        deduct_count: deductCount,
        ...(notes ? { notes } : {}),
      }),
    });

    const data = await res.json();

    if (res.status === 402) throw new Error("NO_TOKENS");
    if (res.status === 401) throw new Error("Session expired. Please sign in again.");
    if (!res.ok) throw new Error(data.error || "Evaluation failed. Please try again.");

    if (data.new_balance !== undefined) refreshBalance();

    return data.text || "";
  };


  const extractTextFromFile = async (fileArg, mediaTypeArg, base64Arg, purpose = "submission") => {
    const ocrRoute = await processOCR(
      fileArg, mediaTypeArg, base64Arg,
      (msg) => setLoadingStep(msg)
    );

    // Surface PDF truncation warning — scoped to whichever document was read
    if (ocrRoute.truncated) {
      if (purpose === "rubric") setRubricPdfTruncated({ total: ocrRoute.totalPages });
      else setPdfTruncated({ total: ocrRoute.totalPages });
    }

    const ocrInstruction = `Extract all text from this document exactly as it appears. Preserve paragraph breaks. Mark any unreadable sections with [ILLEGIBLE].

Return ONLY this JSON (no markdown, no extra text):
{
  "isHandwritten": <true if primarily handwriting, false if primarily typed/printed>,
  "hasExcludedTypedText": false,
  "excludedTextNote": "",
  "hasIllegibleSections": <true if any [ILLEGIBLE] markers present>,
  "illegibleNote": "<brief note about illegible sections, or empty string>",
  "text": "<all extracted text, preserving paragraphs>"
}`;

    // Build message — for raw PDF fallback send as document type,
    // for all image paths (compressed images + rendered PDF pages) send as images.
    let msgContent;
    if (ocrRoute.method === "claude_pdf_raw") {
      msgContent = [
        { type: "document", source: { type: "base64", media_type: "application/pdf", data: ocrRoute.fileBase64 } },
        { type: "text", text: ocrInstruction },
      ];
    } else {
      msgContent = [
        ...ocrRoute.pages.map(b64 => ({
          type: "image",
          source: { type: "base64", media_type: "image/jpeg", data: b64 },
        })),
        { type: "text", text: ocrInstruction },
      ];
    }

    const systemPromptForOCR = purpose === "rubric"
      ? "You are a precise document reader extracting a teacher's grading rubric or assignment criteria. Extract only the rubric/criteria text, exactly as written. Return ONLY valid JSON with no markdown fences."
      : "You are a precise document reader for student writing submissions. Extract only the student's written work. Return ONLY valid JSON with no markdown fences.";

    // OCR call — no token deduction (covered by base eval token)
    const raw = await callClaude(
      [{ role: "user", content: msgContent }],
      systemPromptForOCR,
      2000,
      0
    );

    console.log("[OCR] raw response length:", raw?.length, "| preview:", raw?.slice(0, 200));

    try {
      const clean  = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      console.log("[OCR] parsed text length:", parsed?.text?.length);
      return { ...parsed, extraTokens: 0 };
    } catch {
      console.log("[OCR] JSON parse failed, using raw text");
      return {
        text: raw, isHandwritten: false,
        hasExcludedTypedText: false, excludedTextNote: "",
        hasIllegibleSections: false, illegibleNote: "",
        extraTokens: 0,
      };
    }
  };

  const evaluate = async () => {

    setError(""); setResult(null);
    setLoading(true);

    // ── Block: 5-paragraph essay submitted as image ───────────────────────────
    const preCheckMode = confirmedMode || (countParagraphs(text) >= 2 ? "essay" : "paragraph");
    if (preCheckMode === "essay" && inputMode === "upload" && isImageUpload(fileMediaType)) {
      setError("5-paragraph essays must be submitted as typed text, a Word document, or a PDF. Images are not supported for this format.");
      setLoading(false);
      return;
    }

    // ── Guard: not enough tokens even for minimum cost ────────────────────────
    if (tokenBalance !== null && tokenBalance <= 0) {
      setShowTokenModal(true);
      setLoading(false);
      return;
    }

    try {
      let writingText     = text;
      let ocrExtraTokens  = 0;

      if (inputMode === "upload") {
        if (fileMediaType === "text/plain" || fileMediaType === "docx") {
          writingText = fileText || "";
        } else {
          setLoadingStep("Reading your document…");
          const ocrResult    = await extractTextFromFile(file, fileMediaType, fileBase64, "submission");
          writingText        = ocrResult.text || "";
          ocrExtraTokens     = ocrResult.extraTokens || 0;

          if (ocrResult.hasExcludedTypedText) {
            setHasExcludedTypedText(true);
            setExcludedTextNote(ocrResult.excludedTextNote || "");
          }
        }
        if (!writingText.trim()) { setError("Could not extract text. Please try again."); setLoading(false); return; }

        const wc = countWords(writingText);
        if (wc > WORD_LIMIT) {
          setError(`This document contains ${wc.toLocaleString()} words, which exceeds the ${WORD_LIMIT.toLocaleString()}-word limit. Please shorten or split the writing and try again.`);
          setLoading(false);
          return;
        }

        setText(writingText);
      }

      if (!writingText.trim()) { setError("Please enter or upload some writing first."); setLoading(false); return; }

      // ── Resolve optional teacher-provided rubric (text or file) ──────────────
      let resolvedRubricText = "";
      if (showCustomRubric) {
        if (rubricInputMode === "type") {
          resolvedRubricText = rubricText.trim();
        } else if (rubricFile) {
          if (rubricFileMediaType === "text/plain" || rubricFileMediaType === "docx") {
            resolvedRubricText = (rubricFileText || "").trim();
          } else {
            setLoadingStep("Reading teacher rubric…");
            const rubricOcr = await extractTextFromFile(rubricFile, rubricFileMediaType, rubricFileBase64, "rubric");
            resolvedRubricText = (rubricOcr.text || "").trim();
          }
        }
      }
      const hasCustomRubric = showCustomRubric && resolvedRubricText.length > 0;

      const wordCount      = countWords(writingText);
      const baseTokens     = calcBaseEvalTokens(wordCount);
      const totalTokens    = baseTokens + ocrExtraTokens + (hasCustomRubric ? 1 : 0);
      const maxOutputTokens = getMaxOutputTokens(grade, wordCount);

      // ── Guard: check total tokens needed after OCR reveals word count ────────
      if (tokenBalance !== null && tokenBalance < totalTokens) {
        setShowTokenModal(true);
        setLoading(false);
        return;
      }

      // ── Grade 2–7 word count warning (non-blocking) ──────────────────────────
      if (grade <= 7 && wordCount > WORD_THRESHOLD_GRADE27_WARN) {
        setWordCountWarning(wordCount);
      }

      const clientDetected = countParagraphs(writingText) >= 2 ? "essay" : "paragraph";
      const mode = confirmedMode || clientDetected;

      const gradeLabel = GRADES.find(g => g.value === grade)?.label || `Grade ${grade}`;

      setLoadingStep("Evaluating your writing…");

      // ── Grade-specific coaching profile ────────────────────────────────────
      const gradeProfile = getGradeProfile(grade);

      const systemPrompt = `You are an expert, encouraging writing coach evaluating student writing for ${gradeLabel}.
${gradeProfile.coachingVoice}
${gradeProfile.feedbackInstructions}
If the writing quality is clearly above the expected level for ${gradeLabel}, include one encouraging sentence in coachFeedback noting this — e.g. "Your writing shows skills beyond what's typically expected at this grade level."
If the writing quality is clearly below the expected level for ${gradeLabel}, include one supportive sentence in coachFeedback noting this — e.g. "This writing is still developing some skills we'd expect to see at this grade level — but every great writer starts somewhere, and the important thing is to keep practicing."
CRITICAL: You are ONLY evaluating the student's writing as submitted. Do NOT rewrite sentences, add content, or complete their writing.
Respond ONLY with valid JSON. No markdown fences, no preamble.`;

      // ── Rubric JSON schemas ──────────────────────────────────────────────────
      const paragraphRubricJSON = `{
  "criteriaRatings": {
    "topic_sentence": <1.0–5.0>,
    "supporting_sentences": <1.0–5.0>,
    "conclusion_sentence": <1.0–5.0>
  },
  "criteriaFeedback": {
    "topic_sentence": "<1 sentence coach note>",
    "supporting_sentences": "<1 sentence coach note>",
    "conclusion_sentence": "<1 sentence coach note>"
  }
}`;

      const essayRubricJSON = `{
  "criteriaRatings": {
    "hook": <1.0–5.0>,
    "background": <1.0–5.0>,
    "thesis": <1.0–5.0>,
    "topic_sentence": <1.0–5.0>,
    "evidence": <1.0–5.0>,
    "analysis": <1.0–5.0>,
    "transition": <1.0–5.0>,
    "restate_thesis": <1.0–5.0>,
    "synthesize": <1.0–5.0>,
    "final_thought": <1.0–5.0>
  },
  "criteriaFeedback": {
    "hook": "<1 sentence coach note on hook>",
    "background": "<1 sentence coach note on background info>",
    "thesis": "<1 sentence — does thesis list all 3 supporting points?>",
    "topic_sentence": "<1 sentence — average across body paragraphs>",
    "evidence": "<1 sentence — are quotes/data cited with MLA parenthetical citations like (Author ##)?>",
    "analysis": "<1 sentence — does analysis explain HOW evidence supports the thesis?>",
    "transition": "<1 sentence — do transitions bridge effectively between paragraphs?>",
    "restate_thesis": "<1 sentence — is thesis restated with different phrasing, not copied?>",
    "synthesize": "<1 sentence — are all 3 body points briefly tied together?>",
    "final_thought": "<1 sentence — does the conclusion leave a lasting impression or real-world connection?>"
  }
}`;

      const creativeRubricJSON = `{
  "criteriaRatings": {
    "voice_style": <1.0–5.0>,
    "story_structure": <1.0–5.0>,
    "descriptive_detail": <1.0–5.0>,
    "character_scene": <1.0–5.0>,
    "coherence_flow": <1.0–5.0>
  },
  "criteriaFeedback": {
    "voice_style": "<1 sentence coach note>",
    "story_structure": "<1 sentence coach note>",
    "descriptive_detail": "<1 sentence coach note>",
    "character_scene": "<1 sentence coach note>",
    "coherence_flow": "<1 sentence coach note>"
  }
}`;

      const unstructuredRubricJSON = `{
  "criteriaRatings": {
    "clarity_ideas": <1.0–5.0>,
    "supporting_detail": <1.0–5.0>,
    "organization": <1.0–5.0>,
    "voice_engagement": <1.0–5.0>
  },
  "criteriaFeedback": {
    "clarity_ideas": "<1 sentence coach note>",
    "supporting_detail": "<1 sentence coach note>",
    "organization": "<1 sentence coach note>",
    "voice_engagement": "<1 sentence coach note>"
  }
}`;

      // ── Style/literary JSON (Grade 6+) ───────────────────────────────────────
      const styleJSON = grade >= 6 ? `,
  "styleRatings": {
    "sentence_variety": <1.0–5.0>,
    "word_choice": <1.0–5.0>,
    "voice_consistency": <1.0–5.0>,
    "transitions_flow": <1.0–5.0>${grade >= 8 ? ',\n    "mla_citation_format": <1.0–5.0>' : ""}
  }` : "";

      const literaryJSON = grade >= 6 ? `,
  "styleRatings": {
    "metaphor_imagery": <1.0–5.0>,
    "dialogue_pacing": <1.0–5.0>,
    "foreshadowing_tension": <1.0–5.0>${grade >= 8 ? ',\n    "symbolism_theme": <1.0–5.0>' : ""}
  }` : "";

      // ── Structure guides ─────────────────────────────────────────────────────
      const essayStructureGuide = getGradeEssayGuide(grade);

      const paragraphStructureGuide = getGradeParagraphGuide(grade);

      const creativeStructureGuide = getGradeCreativeGuide(grade);

      const unstructuredStructureGuide = getGradeUnstructuredGuide(grade);

      // ── Format detection instruction (when user hasn't confirmed a mode) ─────
      const formatDetectionNote = !confirmedMode ? `
FORMAT DETECTION: First determine which format best describes this writing, then evaluate under that format.
- "paragraph": A single structured paragraph with topic sentence, support, and clincher
- "essay": Multi-paragraph essay with introduction, body paragraphs, and conclusion  
- "creative": Creative or narrative writing — story, fiction, personal narrative, or imaginative writing
- "unstructured": Reflective, journal-style, opinion, or free-form writing not following a specific academic format
Set "detectedMode" in your response to whichever format you selected.` : "";

      // ── Select rubric guide and JSON schema based on mode ───────────────────
      const structureGuide = mode === "essay" ? essayStructureGuide
        : mode === "creative" ? creativeStructureGuide
        : mode === "unstructured" ? unstructuredStructureGuide
        : paragraphStructureGuide;

      const rubricJSON = mode === "essay" ? essayRubricJSON
        : mode === "creative" ? creativeRubricJSON
        : mode === "unstructured" ? unstructuredRubricJSON
        : paragraphRubricJSON;

      const extraStyleJSON = mode === "creative" ? literaryJSON
        : (mode === "essay" || mode === "paragraph") ? styleJSON
        : ""; // unstructured gets no style section

      // ── Optional teacher-provided rubric (additional, alongside the grade-level rubric) ──
      const teacherRubricJSON = hasCustomRubric ? `,
  "teacherRubricFeedback": {
    "alignmentScore": <1.0–5.0>,
    "summary": "<2–3 sentences evaluating the writing specifically against the teacher's rubric below>",
    "metCriteria": ["<criterion the writing satisfies>", "..."],
    "missedCriteria": ["<criterion not yet met or underdeveloped>", "..."]
  }` : "";

      const teacherRubricBlock = hasCustomRubric ? `

ADDITIONAL TEACHER-PROVIDED RUBRIC:
The student's teacher supplied the following grading rubric or assignment criteria. Evaluate the writing against BOTH the grade-level rubric above AND this teacher rubric, reporting results for each separately.
---
${resolvedRubricText}
---` : "";

      const modeLabel = mode === "essay" ? "5-Paragraph Essay"
        : mode === "creative" ? "Creative Narrative"
        : mode === "unstructured" ? "Unstructured / Free Write"
        : "Single Paragraph";

      const evalPrompt = `Evaluate this ${gradeLabel} student's writing submission.
${confirmedMode ? `Format: ${modeLabel}` : "Determine the format first, then evaluate."}
${formatDetectionNote}
${structureGuide}
${teacherRubricBlock}

WRITING TO EVALUATE:
---
${writingText}
---

Return a single JSON object with EXACTLY this shape:

{
  "detectedMode": "${confirmedMode || "paragraph|essay|creative|unstructured"}",
  "overallScore": <number 1.0–5.0>,
  "hasIllegibleSections": <true/false>,
  "illegibleNote": "<brief note if illegible sections exist, otherwise empty string>",
  "writingAbout": "<2–3 sentences describing what this writing communicates — written as a reader's summary>",
  "coachFeedback": "<3–4 paragraph encouraging coach narrative: specific praise first, then targeted growth areas, always ending on an encouraging note. Do NOT rewrite student content.>",
  "strengths": ["<specific strength 1>", "<specific strength 2>", "<specific strength 3>"],
  "opportunities": ["<specific growth area 1>", "<specific growth area 2>", "<specific growth area 3>"],
  ${rubricJSON}${extraStyleJSON}${teacherRubricJSON},
  "annotated": {
    "segments": [
      // Reproduce the COMPLETE original text broken into segments.
      // Each segment is either:
      //   { "type": "original", "text": "..." }
      //   { "type": "annotated", "text": "...", "annotationType": "strength|improve|structure|style", "comment": "<coach note>" }
      // Rules: annotate 8–14 phrases. Comments must be coach-voiced, not rewriting the student's work.
      // ALL original text must appear across all segments combined.
    ]
  }
}`;

      const evalNotes = hasCustomRubric ? "Writing evaluation + teacher rubric" : undefined;
      const raw = await callClaude([{ role: "user", content: evalPrompt }], systemPrompt, maxOutputTokens, totalTokens, evalNotes);
      let parsed;
      try {
        const clean = raw.replace(/```json|```/g, "").trim();
        parsed = JSON.parse(clean);
      } catch {
        setError("Evaluation returned an unexpected format. Please try again.");
        setLoading(false);
        return;
      }

      // Use Claude's detected mode if user hadn't confirmed one
      if (!confirmedMode && parsed.detectedMode) {
        setDetectedMode(parsed.detectedMode);
      } else {
        setDetectedMode(clientDetected);
      }

      setResult(parsed);
      setTab("feedback");
    } catch (err) {
      if (err.message === "NO_TOKENS") {
        setShowTokenModal(true);
      } else {
        setError("Something went wrong: " + err.message);
      }
    }
    setLoading(false);
    setLoadingStep("");
  };

  const comparePrompt = async () => {
    if (!prompt.trim() || !result) return;
    setLoading(true); setLoadingStep("Comparing to your prompt…");
    try {
      const raw = await callClaude(
        [{ role: "user", content: `A student was given this writing prompt or question:\n"${prompt}"\n\nA reader summarized the student's writing as:\n"${result.writingAbout}"\n\nFull submitted writing:\n---\n${text}\n---\n\nReturn JSON only:\n{\n  "alignmentScore": <1–5>,\n  "alignmentSummary": "<2–3 sentences: how well does the writing address the prompt/question?>",\n  "matchedElements": ["<element the writing addressed well>", "..."],\n  "missedElements": ["<element not yet addressed or underdeveloped>", "..."]\n}` }],
        "You are a writing coach. Respond ONLY with valid JSON.",
        800
      );
      const clean = raw.replace(/```json|```/g, "").trim();
      setPromptResult(JSON.parse(clean));
    } catch (e) { setError("Comparison failed: " + e.message); }
    setLoading(false); setLoadingStep("");
  };

  // ─── Draft-workspace tools — on-demand, coach-only, never rewrite text ────
  const DRAFT_TOOL_GUARDRAIL = "Do NOT rewrite sentences, add content, or complete the student's writing — only describe issues and coach the student toward fixing them on their own.";

  const checkGrammar = async () => {
    if (!text.trim() || draftChecking) return;
    setDraftChecking("grammar"); setDraftCheckError("");
    try {
      const gradeLabel = GRADES.find(g => g.value === grade)?.label || `Grade ${grade}`;
      const grammarGuide = getGradeGrammarGuide(grade);
      const raw = await callClaude(
        [{ role: "user", content: `Review this ${gradeLabel} student's in-progress draft for grammar and mechanics issues only.\n\n${grammarGuide}\n\nDRAFT:\n---\n${text}\n---\n\nReturn JSON only:\n{\n  "issues": [\n    { "quote": "<exact phrase from the draft>", "issue": "<short category, e.g. subject-verb agreement>", "explanation": "<1 sentence coach note on what's wrong and how to think about fixing it>" }\n  ]\n}\nList at most 8 issues. If there are no notable issues, return an empty array.` }],
        `You are an encouraging writing coach reviewing a student's in-progress draft for grammar and mechanics only. ${DRAFT_TOOL_GUARDRAIL} Respond ONLY with valid JSON.`,
        1200,
        DRAFT_CHECK_TOKEN_COST,
        "Draft check: grammar"
      );
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setDraftChecks(prev => [{ id: Date.now(), type: "grammar", label: "✅ Grammar Check", content: parsed.issues || [] }, ...prev]);
    } catch (e) {
      if (e.message === "NO_TOKENS") setShowTokenModal(true);
      else setDraftCheckError("Grammar check failed: " + e.message);
    }
    setDraftChecking(null);
  };

  const getSentenceFeedback = async () => {
    if (!selectionRange?.text?.trim() || draftChecking) return;
    setDraftChecking("selection"); setDraftCheckError("");
    try {
      const gradeLabel = GRADES.find(g => g.value === grade)?.label || `Grade ${grade}`;
      const raw = await callClaude(
        [{ role: "user", content: `A ${gradeLabel} student highlighted this passage from their in-progress draft and wants feedback on just this part:\n---\n${selectionRange.text}\n---\n\nReturn JSON only:\n{\n  "strengths": ["<specific strength of this passage>", "..."],\n  "suggestions": ["<specific suggestion for improving clarity, word choice, or structure>", "..."]\n}\nKeep each list to 1–3 items.` }],
        `You are an encouraging writing coach giving targeted feedback on a single highlighted passage from a student's in-progress draft. ${DRAFT_TOOL_GUARDRAIL} Respond ONLY with valid JSON.`,
        800,
        DRAFT_CHECK_TOKEN_COST,
        "Draft check: sentence feedback"
      );
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setDraftChecks(prev => [{ id: Date.now(), type: "selection", label: "🔎 Feedback on Selection", content: parsed, quote: selectionRange.text }, ...prev]);
    } catch (e) {
      if (e.message === "NO_TOKENS") setShowTokenModal(true);
      else setDraftCheckError("Feedback failed: " + e.message);
    }
    setDraftChecking(null);
  };

  const getRevisionIdeas = async () => {
    if (!text.trim() || draftChecking) return;
    setDraftChecking("revision"); setDraftCheckError("");
    try {
      const gradeLabel = GRADES.find(g => g.value === grade)?.label || `Grade ${grade}`;
      const raw = await callClaude(
        [{ role: "user", content: `A ${gradeLabel} student is still drafting and wants quick ideas for strengthening their writing before they finish it.\n\nDRAFT:\n---\n${text}\n---\n\nReturn JSON only:\n{\n  "ideas": ["<short, specific, actionable revision idea>", "..."]\n}\nGive 3–5 ideas.` }],
        `You are an encouraging writing coach giving a quick, lightweight revision-ideas pass on a student's in-progress draft — this is NOT a full rubric evaluation. ${DRAFT_TOOL_GUARDRAIL} Respond ONLY with valid JSON.`,
        900,
        DRAFT_CHECK_TOKEN_COST,
        "Draft check: revision ideas"
      );
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setDraftChecks(prev => [{ id: Date.now(), type: "revision", label: "🪄 Quick Revision Ideas", content: parsed.ideas || [] }, ...prev]);
    } catch (e) {
      if (e.message === "NO_TOKENS") setShowTokenModal(true);
      else setDraftCheckError("Revision ideas failed: " + e.message);
    }
    setDraftChecking(null);
  };

  const reset = () => {
    setText(""); setFile(null); setFilePreview(null); setFileBase64(null);
    setFileMediaType(null); setFileText(null); setHasDocxArtifacts(false);
    setHasExcludedTypedText(false); setExcludedTextNote("");
    setIsDragging(false); setDetectedMode(null); setConfirmedMode(null);
    setResult(null); setShowComparison(false); setPrompt(""); setPromptResult(null);
    setError(""); setTab("feedback");
    setPdfTruncated(null); setWordCountWarning(null);
    setShowCustomRubric(false); setRubricInputMode("type"); setRubricText("");
    setRubricFile(null); setRubricFilePreview(null); setRubricFileBase64(null);
    setRubricFileMediaType(null); setRubricFileText(null); setRubricIsDragging(false);
    setRubricError(""); setRubricPdfTruncated(null);
    setDraftChecks([]); setDraftChecking(null); setDraftCheckError(""); setSelectionRange(null);
  };

  const mode = confirmedMode || detectedMode;
  const rubric = mode === "essay" ? ESSAY_RUBRIC
    : mode === "creative" ? CREATIVE_RUBRIC
    : mode === "unstructured" ? UNSTRUCTURED_RUBRIC
    : PARAGRAPH_RUBRIC;

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #0d1117 0%, #161b22 60%, #0d1117 100%)",
      fontFamily: "'DM Sans', sans-serif", color: "#e6edf3",
      padding: "0 0 80px",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=DM+Serif+Display&display=swap" rel="stylesheet" />

      {/* ── Header ── */}
      <div style={{
        background: "#0d1117", borderBottom: "1px solid #21262d",
        padding: "0 32px", display: "flex", alignItems: "center",
        justifyContent: "space-between", height: 60,
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: "linear-gradient(135deg, #1f6feb, #388bfd)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
          }}>✍️</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: -0.3 }}>Writing Coach</div>
            <div style={{ fontSize: 10, color: "#8b949e", marginTop: -2, letterSpacing: 0.5 }}>EVALUATE · ANNOTATE · GROW</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {tokenBalance !== null && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              background: tokenBalance === 0 ? "rgba(248,81,73,0.1)" : "rgba(56,139,253,0.08)",
              border: `1px solid ${tokenBalance === 0 ? "#f85149" : "#21262d"}`,
              borderRadius: 7, padding: "5px 11px",
            }}>
              <span style={{ fontSize: 13 }}>🪙</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: tokenBalance === 0 ? "#f85149" : tokenBalance <= 2 ? "#fb923c" : "#8b949e" }}>
                {tokenBalance} {tokenBalance === 1 ? "token" : "tokens"}
              </span>
              <button onClick={() => setShowTokenModal(true)} style={{ background: "transparent", border: "none", color: "#388bfd", fontSize: 11, cursor: "pointer", fontWeight: 700, padding: "0 0 0 4px" }}>
                + Buy
              </button>
            </div>
          )}
          {result && (
            <button onClick={reset} style={{ background: "transparent", border: "1px solid #30363d", color: "#8b949e", borderRadius: 7, padding: "5px 13px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              ↩ Start Over
            </button>
          )}
          <button onClick={() => setShowGuide(true)} style={{ background: "transparent", border: "none", color: "#484f58", fontSize: 11, cursor: "pointer", fontWeight: 500, padding: "5px 4px" }} title="Parent & Student Guide">
            ❔ Guide
          </button>
          <button onClick={signOut} style={{ background: "transparent", border: "none", color: "#484f58", fontSize: 11, cursor: "pointer", fontWeight: 500, padding: "5px 4px" }} title="Sign out">
            Sign out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 820, margin: "0 auto", padding: "28px 20px 0" }}>

        {/* ══ SETUP PANEL ══ */}
        {!result && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <h1 style={{
                fontFamily: "'DM Serif Display', serif", fontSize: 36, fontWeight: 400,
                margin: "0 0 8px",
                background: "linear-gradient(100deg, #e6edf3 40%, #8b949e)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>Your Writing, Evaluated</h1>
              <p style={{ color: "#8b949e", fontSize: 14, margin: 0 }}>
                Honest, encouraging feedback — no ghostwriting, just coaching.
              </p>
            </div>

            {/* Grade */}
            <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: "18px 22px", marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>Grade Level</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {GRADES.map(g => (
                  <button key={g.value} onClick={() => setGrade(g.value)} style={{
                    padding: "6px 13px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                    border: grade === g.value ? "1px solid #388bfd" : "1px solid #30363d",
                    background: grade === g.value ? "rgba(56,139,253,0.15)" : "transparent",
                    color: grade === g.value ? "#388bfd" : "#8b949e",
                    fontWeight: grade === g.value ? 700 : 400, transition: "all 0.15s",
                  }}>{g.label}</button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: "18px 22px", marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[["type", "✏️ Type / Paste"], ["upload", "📄 Upload File"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setInputMode(val)} style={{
                    padding: "7px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    border: inputMode === val ? "1px solid #388bfd" : "1px solid #30363d",
                    background: inputMode === val ? "rgba(56,139,253,0.15)" : "transparent",
                    color: inputMode === val ? "#388bfd" : "#8b949e",
                    fontWeight: inputMode === val ? 700 : 400,
                  }}>{lbl}</button>
                ))}
              </div>

              {inputMode === "type" ? (
                <>
                  <textarea
                    value={text}
                    onChange={e => { setText(e.target.value); setDetectedMode(null); setConfirmedMode(null); setSelectionRange(null); }}
                    onSelect={e => {
                      const { selectionStart, selectionEnd } = e.target;
                      setSelectionRange(selectionStart !== selectionEnd
                        ? { start: selectionStart, end: selectionEnd, text: text.slice(selectionStart, selectionEnd) }
                        : null);
                    }}
                    placeholder="Type or paste your writing here…"
                    style={{
                      width: "100%", minHeight: 220, background: "#0d1117",
                      border: `1px solid ${countWords(text) > WORD_LIMIT ? "#f87171" : "#30363d"}`,
                      borderRadius: 8, color: "#e6edf3",
                      fontSize: 15, fontFamily: "'DM Serif Display', serif",
                      padding: "14px 16px", resize: "vertical", outline: "none",
                      lineHeight: 1.9, boxSizing: "border-box",
                    }}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      color: countWords(text) > WORD_LIMIT ? "#f87171" : countWords(text) > WORD_LIMIT * 0.9 ? "#fb923c" : "#484f58",
                    }}>
                      {countWords(text).toLocaleString()} / {WORD_LIMIT.toLocaleString()} words
                      {countWords(text) > WORD_LIMIT ? " — over limit" : ""}
                    </span>
                  </div>

                  {/* ── Draft-workspace tools — on-demand, coach-only help while drafting ── */}
                  {(() => {
                    const draftToolBtn = (disabled) => ({
                      padding: "7px 13px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: "1px solid #30363d", background: "transparent",
                      color: disabled ? "#484f58" : "#8b949e",
                      cursor: disabled ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 6,
                    });
                    const grammarDisabled   = !text.trim() || !!draftChecking || tokenBalance === 0;
                    const selectionDisabled = !selectionRange?.text?.trim() || !!draftChecking || tokenBalance === 0;
                    const revisionDisabled  = !text.trim() || !!draftChecking || tokenBalance === 0;
                    return (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #21262d" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 10 }}>
                          While You're Drafting
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: (draftChecks.length || draftCheckError) ? 12 : 0 }}>
                          <button onClick={checkGrammar} disabled={grammarDisabled} style={draftToolBtn(grammarDisabled)}>
                            {draftChecking === "grammar" ? <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> : "✅"} Check Grammar
                            <span style={{ color: "#484f58" }}>· {DRAFT_CHECK_TOKEN_COST} 🪙</span>
                          </button>
                          <button onClick={getSentenceFeedback} disabled={selectionDisabled} style={draftToolBtn(selectionDisabled)} title={!selectionRange?.text?.trim() ? "Highlight a sentence in your draft first" : undefined}>
                            {draftChecking === "selection" ? <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> : "🔎"} Feedback on Selection
                            <span style={{ color: "#484f58" }}>· {DRAFT_CHECK_TOKEN_COST} 🪙</span>
                          </button>
                          <button onClick={getRevisionIdeas} disabled={revisionDisabled} style={draftToolBtn(revisionDisabled)}>
                            {draftChecking === "revision" ? <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> : "🪄"} Quick Revision Ideas
                            <span style={{ color: "#484f58" }}>· {DRAFT_CHECK_TOKEN_COST} 🪙</span>
                          </button>
                        </div>

                        {draftCheckError && (
                          <div style={{ fontSize: 12, color: "#f85149", marginBottom: 10 }}>⚠ {draftCheckError}</div>
                        )}

                        {draftChecks.map(check => (
                          <div key={check.id} style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, padding: "11px 14px", marginBottom: 8, fontSize: 13 }}>
                            <div style={{ fontWeight: 700, marginBottom: 6, color: "#e6edf3" }}>{check.label}</div>
                            {check.type === "grammar" && (
                              check.content.length === 0
                                ? <div style={{ color: "#4ade80" }}>No notable grammar issues found.</div>
                                : check.content.map((iss, i) => (
                                    <div key={i} style={{ marginBottom: 6, lineHeight: 1.5 }}>
                                      <span style={{ color: "#fb923c", fontWeight: 600 }}>{iss.issue}:</span>{" "}
                                      <span style={{ color: "#8b949e" }}>"{iss.quote}"</span> — {iss.explanation}
                                    </div>
                                  ))
                            )}
                            {check.type === "selection" && (
                              <div>
                                <div style={{ color: "#484f58", fontStyle: "italic", marginBottom: 6 }}>"{check.quote}"</div>
                                {(check.content.strengths || []).map((s, i) => <div key={"s" + i} style={{ color: "#4ade80", marginBottom: 4 }}>+ {s}</div>)}
                                {(check.content.suggestions || []).map((s, i) => <div key={"u" + i} style={{ color: "#fb923c", marginBottom: 4 }}>→ {s}</div>)}
                              </div>
                            )}
                            {check.type === "revision" && (
                              check.content.map((idea, i) => <div key={i} style={{ marginBottom: 4, lineHeight: 1.5 }}>• {idea}</div>)
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      border: `2px dashed ${isDragging ? "#388bfd" : "#30363d"}`,
                      borderRadius: 10,
                      padding: "28px 20px",
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "border-color 0.2s, background 0.2s",
                      background: isDragging ? "rgba(56,139,253,0.06)" : "transparent",
                    }}
                    onMouseEnter={e => { if (!isDragging) e.currentTarget.style.borderColor = "#388bfd"; }}
                    onMouseLeave={e => { if (!isDragging) e.currentTarget.style.borderColor = "#30363d"; }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 6 }}>
                      {isDragging ? "📂" : "📎"}
                    </div>
                    <div style={{ color: isDragging ? "#388bfd" : "#8b949e", fontSize: 14, fontWeight: isDragging ? 600 : 400 }}>
                      {isDragging
                        ? "Drop it here!"
                        : <><strong style={{ color: "#e6edf3" }}>Click to upload</strong> or drag &amp; drop a file</>
                      }
                    </div>
                    <div style={{ color: "#484f58", fontSize: 12, marginTop: 5 }}>
                      PDF · Word (.docx) · Text (.txt) · JPG · PNG · WEBP
                    </div>
                    <div style={{ color: "#484f58", fontSize: 11, marginTop: 2 }}>
                      Typed or handwritten — OCR will extract the text
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt,image/*,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  {file && (
                    <div style={{ marginTop: 10, padding: "9px 13px", background: "#0d1117", borderRadius: 7, border: "1px solid #30363d", fontSize: 12, color: "#8b949e", display: "flex", alignItems: "center", gap: 8 }}>
                      <span>
                        {fileMediaType === "docx" ? "📝" : fileMediaType === "text/plain" ? "🔤" : "📄"}
                      </span>
                      <span>{file.name} ({(file.size / 1024).toFixed(0)} KB)</span>
                      {fileText && (
                        <span style={{
                          fontSize: 11, fontWeight: 600,
                          color: countWords(fileText) > WORD_LIMIT ? "#f87171" : "#8b949e",
                        }}>
                          {countWords(fileText).toLocaleString()} words
                          {countWords(fileText) > WORD_LIMIT ? " — over limit" : ""}
                        </span>
                      )}
                      {file && (
                        <span style={{ marginLeft: "auto", color: "#4ade80", fontSize: 11, fontWeight: 600 }}>✓ Ready</span>
                      )}
                    </div>
                  )}
                  {filePreview && <img src={filePreview} alt="preview" style={{ marginTop: 10, maxWidth: "100%", borderRadius: 8, border: "1px solid #30363d", maxHeight: 180, objectFit: "contain" }} />}
                </div>
              )}

              {/* Format detect + confirm */}
              {inputMode === "type" && text.trim().length > 30 && (
                <div style={{ marginTop: 13, padding: "11px 15px", background: "#0d1117", borderRadius: 8, border: "1px solid #30363d", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, color: "#8b949e" }}>
                    {confirmedMode
                      ? <>Format: <strong style={{ color: "#e6edf3" }}>{WRITING_MODES.find(m => m.value === confirmedMode)?.label}</strong></>
                      : <>Auto-detected: <strong style={{ color: "#e6edf3" }}>{countParagraphs(text) >= 2 ? "5-Paragraph Essay" : "Single Paragraph"}</strong></>
                    }
                  </span>
                  <span style={{ color: "#484f58", fontSize: 12 }}>Override:</span>
                  {WRITING_MODES.map(m => (
                    <button key={m.value} onClick={() => setConfirmedMode(confirmedMode === m.value ? null : m.value)} style={{
                      padding: "4px 11px", borderRadius: 16, fontSize: 12, cursor: "pointer",
                      border: confirmedMode === m.value ? "1px solid #4ade80" : "1px solid #30363d",
                      background: confirmedMode === m.value ? "rgba(74,222,128,0.12)" : "transparent",
                      color: confirmedMode === m.value ? "#4ade80" : "#8b949e",
                    }}>{m.label}</button>
                  ))}
                </div>
              )}
            </div>

            {/* Optional: teacher-provided rubric */}
            <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: "18px 22px", marginBottom: 14 }}>
              <button
                onClick={() => setShowCustomRubric(!showCustomRubric)}
                style={{
                  background: "transparent", border: "1px solid #30363d",
                  color: "#8b949e", borderRadius: 7, padding: "6px 13px",
                  cursor: "pointer", fontSize: 12, fontWeight: 600,
                }}>
                {showCustomRubric ? "▾" : "▸"} 📋 Add your teacher's rubric <span style={{ color: "#484f58", fontWeight: 400 }}>(optional, +1 token)</span>
              </button>

              {showCustomRubric && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    {[["type", "✏️ Type / Paste"], ["upload", "📄 Upload File"]].map(([val, lbl]) => (
                      <button key={val} onClick={() => setRubricInputMode(val)} style={{
                        padding: "7px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                        border: rubricInputMode === val ? "1px solid #388bfd" : "1px solid #30363d",
                        background: rubricInputMode === val ? "rgba(56,139,253,0.15)" : "transparent",
                        color: rubricInputMode === val ? "#388bfd" : "#8b949e",
                        fontWeight: rubricInputMode === val ? 700 : 400,
                      }}>{lbl}</button>
                    ))}
                  </div>

                  {rubricInputMode === "type" ? (
                    <textarea
                      value={rubricText}
                      onChange={e => setRubricText(e.target.value)}
                      placeholder="Paste your teacher's rubric or grading criteria here…"
                      style={{
                        width: "100%", minHeight: 110, background: "#0d1117",
                        border: "1px solid #30363d", borderRadius: 8,
                        color: "#e6edf3", fontSize: 14, padding: "11px 13px",
                        resize: "vertical", outline: "none", boxSizing: "border-box",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    />
                  ) : (
                    <div>
                      <div
                        onClick={() => rubricFileInputRef.current?.click()}
                        onDragEnter={handleRubricDragEnter}
                        onDragOver={handleRubricDragOver}
                        onDragLeave={handleRubricDragLeave}
                        onDrop={handleRubricDrop}
                        style={{
                          border: `2px dashed ${rubricIsDragging ? "#388bfd" : "#30363d"}`,
                          borderRadius: 10,
                          padding: "22px 18px",
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "border-color 0.2s, background 0.2s",
                          background: rubricIsDragging ? "rgba(56,139,253,0.06)" : "transparent",
                        }}
                        onMouseEnter={e => { if (!rubricIsDragging) e.currentTarget.style.borderColor = "#388bfd"; }}
                        onMouseLeave={e => { if (!rubricIsDragging) e.currentTarget.style.borderColor = "#30363d"; }}
                      >
                        <div style={{ fontSize: 24, marginBottom: 4 }}>
                          {rubricIsDragging ? "📂" : "📎"}
                        </div>
                        <div style={{ color: rubricIsDragging ? "#388bfd" : "#8b949e", fontSize: 13, fontWeight: rubricIsDragging ? 600 : 400 }}>
                          {rubricIsDragging
                            ? "Drop it here!"
                            : <><strong style={{ color: "#e6edf3" }}>Click to upload</strong> or drag &amp; drop the rubric</>
                          }
                        </div>
                        <div style={{ color: "#484f58", fontSize: 11, marginTop: 4 }}>
                          PDF · Word (.docx) · Text (.txt) · JPG · PNG · WEBP
                        </div>
                      </div>
                      <input
                        ref={rubricFileInputRef}
                        type="file"
                        accept=".pdf,.docx,.txt,image/*,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                        onChange={handleRubricFileChange}
                        style={{ display: "none" }}
                      />
                      {rubricFile && (
                        <div style={{ marginTop: 10, padding: "9px 13px", background: "#0d1117", borderRadius: 7, border: "1px solid #30363d", fontSize: 12, color: "#8b949e", display: "flex", alignItems: "center", gap: 8 }}>
                          <span>{rubricFileMediaType === "docx" ? "📝" : rubricFileMediaType === "text/plain" ? "🔤" : "📄"}</span>
                          <span>{rubricFile.name} ({(rubricFile.size / 1024).toFixed(0)} KB)</span>
                          <span style={{ marginLeft: "auto", color: "#4ade80", fontSize: 11, fontWeight: 600 }}>✓ Ready</span>
                        </div>
                      )}
                      {rubricFilePreview && <img src={rubricFilePreview} alt="rubric preview" style={{ marginTop: 10, maxWidth: "100%", borderRadius: 8, border: "1px solid #30363d", maxHeight: 160, objectFit: "contain" }} />}
                    </div>
                  )}

                  {rubricError && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#f85149" }}>⚠ {rubricError}</div>
                  )}
                  {rubricPdfTruncated && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#fb923c" }}>
                      📄 This rubric PDF has {rubricPdfTruncated.total} pages — only the first {PDF_PAGE_CAP} were read.
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div style={{ background: "rgba(248,81,73,0.1)", border: "1px solid #f85149", borderRadius: 8, padding: "11px 15px", color: "#f85149", fontSize: 13, marginBottom: 14 }}>
                ⚠ {error}
              </div>
            )}


            {/* ── Live token cost estimate ─────────────────────────────── */}
            {(() => {
              const liveWc = inputMode === "type"
                ? countWords(text)
                : (fileText ? countWords(fileText) : null);
              const isImageOrPdf = inputMode === "upload" && fileMediaType &&
                !["text/plain", "docx"].includes(fileMediaType);
              const liveBase   = liveWc !== null ? calcBaseEvalTokens(liveWc) : 1;
              const ocrNote    = isImageOrPdf ? " + possible 1–2 for handwriting" : "";
              const liveRubricSurcharge = showCustomRubric &&
                ((rubricInputMode === "type" && rubricText.trim()) ||
                 (rubricInputMode === "upload" && rubricFile)) ? 1 : 0;
              const liveTotal = liveBase + liveRubricSurcharge;
              const showEstimate = (inputMode === "type" && text.trim()) ||
                                   (inputMode === "upload" && (fileText || file));
              if (!showEstimate) return null;
              return (
                <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>🪙</span>
                  <span>
                    This evaluation will use{" "}
                    <strong style={{ color: liveTotal > 1 ? "#fb923c" : "#4ade80" }}>
                      {liveTotal} token{liveTotal > 1 ? "s" : ""}
                    </strong>
                    {ocrNote && <span style={{ color: "#484f58" }}>{ocrNote}</span>}
                    {liveWc !== null && liveWc > WORD_THRESHOLD_2_TOKEN && (
                      <span style={{ color: "#fb923c" }}> (writing exceeds {WORD_THRESHOLD_2_TOKEN} words)</span>
                    )}
                    {liveRubricSurcharge > 0 && (
                      <span style={{ color: "#484f58" }}> (includes +1 for teacher rubric)</span>
                    )}
                  </span>
                </div>
              );
            })()}

            {/* ── Grade 2–7 word count warning (>600 words) ────────────── */}
            {grade <= 7 && (() => {
              const liveWc = inputMode === "type"
                ? countWords(text)
                : (fileText ? countWords(fileText) : null);
              if (liveWc === null || liveWc <= WORD_THRESHOLD_GRADE27_WARN) return null;
              return (
                <div style={{ background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.3)", borderRadius: 8, padding: "10px 14px", color: "#fb923c", fontSize: 13, marginBottom: 10 }}>
                  ⚠️ This writing is {liveWc.toLocaleString()} words — longer than typical for this grade level. Double-check that the right content was submitted.
                </div>
              );
            })()}

            {/* ── 5-paragraph essay + image block warning ──────────────── */}
            {inputMode === "upload" && isImageUpload(fileMediaType) && confirmedMode === "essay" && (
              <div style={{ background: "rgba(248,81,73,0.1)", border: "1px solid #f85149", borderRadius: 8, padding: "10px 14px", color: "#f85149", fontSize: 13, marginBottom: 10 }}>
                ✕ 5-paragraph essays cannot be submitted as images. Please use typed text, a Word document, or a PDF instead.
              </div>
            )}

            {/* Low-balance warning */}
            {tokenBalance !== null && tokenBalance <= 2 && tokenBalance > 0 && (
              <div style={{ background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.3)", borderRadius: 8, padding: "10px 14px", color: "#fb923c", fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>🪙 {tokenBalance} evaluation{tokenBalance === 1 ? "" : "s"} remaining</span>
                <button onClick={() => setShowTokenModal(true)} style={{ background: "transparent", border: "1px solid #fb923c", color: "#fb923c", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Buy Tokens</button>
              </div>
            )}

            <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 10, lineHeight: 1.5 }}>
              ✍️ Submit only your own original writing. Writing Coach evaluates and coaches — it doesn't
              write for you, and using AI to generate or rewrite your submission isn't supported here.
            </div>

            <button
              onClick={tokenBalance === 0 ? () => setShowTokenModal(true) : evaluate}
              disabled={
                loading ||
                (tokenBalance !== 0 && (!text.trim() && !file)) ||
                (inputMode === "type" && countWords(text) > WORD_LIMIT) ||
                (inputMode === "upload" && fileText && countWords(fileText) > WORD_LIMIT) ||
                (inputMode === "upload" && isImageUpload(fileMediaType) && confirmedMode === "essay")
              }
              style={{
                width: "100%", padding: "15px", borderRadius: 10, fontSize: 15, fontWeight: 700,
                background: loading || (tokenBalance !== 0 && (!text.trim() && !file)) || (inputMode === "type" && countWords(text) > WORD_LIMIT) || (inputMode === "upload" && fileText && countWords(fileText) > WORD_LIMIT)
                  ? "#21262d"
                  : tokenBalance === 0
                    ? "linear-gradient(135deg, #b45309, #d97706)"
                    : "linear-gradient(135deg, #1f6feb, #388bfd)",
                color: loading || (tokenBalance !== 0 && (!text.trim() && !file)) || (inputMode === "type" && countWords(text) > WORD_LIMIT) || (inputMode === "upload" && fileText && countWords(fileText) > WORD_LIMIT)
                  ? "#484f58" : "#fff",
                border: "none", cursor: "pointer",
                letterSpacing: 0.3, transition: "all 0.2s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              {loading
                ? <><span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> {loadingStep || "Working…"}</>
                : tokenBalance === 0
                  ? "🪙 No Tokens — Buy More →"
                  : (inputMode === "type" && countWords(text) > WORD_LIMIT) || (inputMode === "upload" && fileText && countWords(fileText) > WORD_LIMIT)
                    ? `Over ${WORD_LIMIT.toLocaleString()}-Word Limit`
                    : "Evaluate My Writing →"}
            </button>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {/* ══ RESULTS PANEL ══ */}
        {result && (
          <div>
            {/* Score banner */}
            <div style={{
              background: "linear-gradient(135deg, #161b22, #1c2128)",
              border: "1px solid #21262d", borderRadius: 14,
              padding: "22px 26px", marginBottom: 16,
              display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14,
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 4 }}>
                  Overall Score · {GRADES.find(g => g.value === grade)?.label} · {mode === "essay" ? "5-Paragraph Essay" : "Single Paragraph"}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <span style={{ fontSize: 50, fontWeight: 700, fontFamily: "'DM Serif Display', serif", color: ratingColor(result.overallScore), lineHeight: 1 }}>
                    {result.overallScore?.toFixed(1)}
                  </span>
                  <span style={{ color: "#8b949e", fontSize: 15 }}>/5.0 — {ratingLabel(result.overallScore)}</span>
                </div>
              </div>
              <DownloadReport result={result} grade={grade} mode={mode} text={text} />
            </div>

            {/* Mode confirm if not confirmed */}
            {!confirmedMode && (
              <div style={{ background: "rgba(56,139,253,0.08)", border: "1px solid rgba(56,139,253,0.3)", borderRadius: 8, padding: "10px 15px", marginBottom: 14, fontSize: 13, color: "#388bfd", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span>Evaluated as <strong>{WRITING_MODES.find(m => m.value === mode)?.label || mode}</strong>. Wrong format?</span>
                {WRITING_MODES.map(m => (
                  <button key={m.value} onClick={() => { setConfirmedMode(m.value); evaluate(); }} style={{
                    padding: "4px 11px", borderRadius: 16, fontSize: 12, cursor: "pointer",
                    border: "1px solid #388bfd", background: "transparent", color: "#388bfd",
                  }}>Re-evaluate as {m.label}</button>
                ))}
              </div>
            )}

            {/* PDF truncation warning */}
            {pdfTruncated && (
              <div style={{ background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.3)", borderRadius: 8, padding: "11px 15px", color: "#fb923c", fontSize: 13, marginBottom: 14 }}>
                📄 This PDF has {pdfTruncated.total} pages — only the first {PDF_PAGE_CAP} were evaluated due to the document length limit. Split the document or paste the text directly to evaluate the full piece.
              </div>
            )}

            {/* Grade 2–7 long writing warning (post-eval) */}
            {wordCountWarning && grade <= 7 && (
              <div style={{ background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.3)", borderRadius: 8, padding: "11px 15px", color: "#fb923c", fontSize: 13, marginBottom: 14 }}>
                ⚠️ This writing was {wordCountWarning.toLocaleString()} words — longer than typical for this grade level. If this seems too long, double-check that only the student's writing was submitted.
              </div>
            )}

            {/* Illegible warning */}
            {result.hasIllegibleSections && (
              <div style={{ background: "rgba(251,146,60,0.1)", border: "1px solid #fb923c", borderRadius: 8, padding: "11px 15px", color: "#fb923c", fontSize: 13, marginBottom: 14 }}>
                ✏️ {result.illegibleNote || "Some sections were difficult to read. Clearer handwriting will improve evaluation accuracy."}
              </div>
            )}

            {/* Handwriting: excluded typed text notice */}
            {hasExcludedTypedText && (
              <div style={{ background: "rgba(96,165,250,0.08)", border: "1px solid #60a5fa", borderRadius: 8, padding: "11px 15px", color: "#60a5fa", fontSize: 13, marginBottom: 14 }}>
                ✍️ Handwritten document detected. Pre-printed text (instructions, prompts, form labels) was excluded — only the handwritten student writing was evaluated.
                {excludedTextNote ? ` ${excludedTextNote}` : ""}
              </div>
            )}

            {/* Word document formatting notice */}
            {hasDocxArtifacts && (
              <div style={{ background: "rgba(96,165,250,0.08)", border: "1px solid #60a5fa", borderRadius: 8, padding: "11px 15px", color: "#60a5fa", fontSize: 13, marginBottom: 14 }}>
                📝 This Word document contained formatting (bold, bullets, headers, etc.) that was stripped before evaluation. Only the plain text was assessed — if something seems off, check that the right content came through.
              </div>
            )}

            {/* Teacher rubric alignment */}
            {result.teacherRubricFeedback && (
              <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: "18px 22px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 8 }}>📋 Teacher Rubric Alignment</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, color: ratingColor(result.teacherRubricFeedback.alignmentScore), fontSize: 17 }}>
                    {result.teacherRubricFeedback.alignmentScore}/5 — {ratingLabel(result.teacherRubricFeedback.alignmentScore)}
                  </span>
                </div>
                <p style={{ fontSize: 14, color: "#c9d1d9", margin: "0 0 10px", lineHeight: 1.6 }}>{result.teacherRubricFeedback.summary}</p>
                {result.teacherRubricFeedback.metCriteria?.length > 0 && (
                  <div style={{ marginBottom: 5 }}>
                    <span style={{ fontSize: 11, color: "#4ade80", fontWeight: 700 }}>✓ MET: </span>
                    <span style={{ fontSize: 13, color: "#8b949e" }}>{result.teacherRubricFeedback.metCriteria.join(" · ")}</span>
                  </div>
                )}
                {result.teacherRubricFeedback.missedCriteria?.length > 0 && (
                  <div>
                    <span style={{ fontSize: 11, color: "#fb923c", fontWeight: 700 }}>→ NOT YET MET: </span>
                    <span style={{ fontSize: 13, color: "#8b949e" }}>{result.teacherRubricFeedback.missedCriteria.join(" · ")}</span>
                  </div>
                )}
              </div>
            )}

            {/* What the writing communicates */}
            <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: "18px 22px", marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 8 }}>📖 What Your Writing Communicates</div>
              <p style={{ margin: "0 0 14px", fontSize: 15, lineHeight: 1.75, color: "#c9d1d9", fontStyle: "italic" }}>"{result.writingAbout}"</p>
              <button
                onClick={() => setShowComparison(!showComparison)}
                style={{
                  background: "transparent", border: "1px solid #30363d",
                  color: "#8b949e", borderRadius: 7, padding: "6px 13px",
                  cursor: "pointer", fontSize: 12, fontWeight: 600,
                }}>
                {showComparison ? "▾ Hide comparison" : "▸ Compare to my prompt or question"}
              </button>
              {showComparison && (
                <div style={{ marginTop: 12 }}>
                  <textarea
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    placeholder="Paste your original writing prompt, question, or assignment here…"
                    style={{
                      width: "100%", minHeight: 72, background: "#0d1117",
                      border: "1px solid #30363d", borderRadius: 8,
                      color: "#e6edf3", fontSize: 14, padding: "11px 13px",
                      resize: "vertical", outline: "none", boxSizing: "border-box",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                  <button onClick={comparePrompt} disabled={loading || !prompt.trim()} style={{
                    marginTop: 7, padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                    background: !prompt.trim() ? "#21262d" : "#1f6feb",
                    color: !prompt.trim() ? "#484f58" : "#fff",
                    border: "none", cursor: !prompt.trim() ? "not-allowed" : "pointer",
                  }}>{loading ? "Comparing…" : "Compare →"}</button>
                  {promptResult && (
                    <div style={{ marginTop: 12, padding: "15px", background: "#0d1117", borderRadius: 9, border: "1px solid #30363d" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#8b949e" }}>Prompt Alignment:</span>
                        <span style={{ fontWeight: 700, color: ratingColor(promptResult.alignmentScore), fontSize: 15 }}>
                          {promptResult.alignmentScore}/5 — {ratingLabel(promptResult.alignmentScore)}
                        </span>
                      </div>
                      <p style={{ fontSize: 14, color: "#c9d1d9", margin: "0 0 10px", lineHeight: 1.6 }}>{promptResult.alignmentSummary}</p>
                      {promptResult.matchedElements?.length > 0 && (
                        <div style={{ marginBottom: 5 }}>
                          <span style={{ fontSize: 11, color: "#4ade80", fontWeight: 700 }}>✓ ADDRESSED: </span>
                          <span style={{ fontSize: 13, color: "#8b949e" }}>{promptResult.matchedElements.join(" · ")}</span>
                        </div>
                      )}
                      {promptResult.missedElements?.length > 0 && (
                        <div>
                          <span style={{ fontSize: 11, color: "#fb923c", fontWeight: 700 }}>→ CONSIDER ADDING: </span>
                          <span style={{ fontSize: 13, color: "#8b949e" }}>{promptResult.missedElements.join(" · ")}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #21262d" }}>
              {[
                ["feedback", "💬 Coach Feedback"],
                ["annotated", "🖊 Annotated Writing"],
                ["structure", "📊 Structure Report"],
              ].map(([val, lbl]) => (
                <button key={val} onClick={() => setTab(val)} style={{
                  padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  background: "transparent", border: "none",
                  borderBottom: tab === val ? "2px solid #388bfd" : "2px solid transparent",
                  color: tab === val ? "#388bfd" : "#8b949e", transition: "all 0.15s",
                }}>{lbl}</button>
              ))}
            </div>

            {/* Tab: Coach Feedback */}
            {tab === "feedback" && (
              <div style={{ background: "#161b22", border: "1px solid #21262d", borderTop: "none", borderRadius: "0 0 12px 12px", padding: "22px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 14 }}>Coach Commentary</div>
                <p style={{ fontSize: 15, lineHeight: 1.9, color: "#c9d1d9", whiteSpace: "pre-wrap", margin: "0 0 22px" }}>{result.coachFeedback}</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#4ade80", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 10 }}>✓ Strengths</div>
                    {(result.strengths || []).map((s, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 13, color: "#c9d1d9", lineHeight: 1.55 }}>
                        <span style={{ color: "#4ade80", flexShrink: 0 }}>✓</span>{s}
                      </div>
                    ))}
                  </div>
                  <div style={{ background: "rgba(251,146,60,0.06)", border: "1px solid rgba(251,146,60,0.2)", borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#fb923c", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 10 }}>→ Opportunities</div>
                    {(result.opportunities || []).map((o, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 13, color: "#c9d1d9", lineHeight: 1.55 }}>
                        <span style={{ color: "#fb923c", flexShrink: 0 }}>→</span>{o}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Annotated Writing */}
            {tab === "annotated" && (
              <div style={{ background: "#161b22", border: "1px solid #21262d", borderTop: "none", borderRadius: "0 0 12px 12px", padding: "22px" }}>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18, alignItems: "center" }}>
                  {[
                    { type: "strength", color: "#4ade80", label: "Strength" },
                    { type: "improve", color: "#fb923c", label: "Improve" },
                    { type: "structure", color: "#60a5fa", label: "Structure" },
                    { type: "style", color: "#c084fc", label: "Style / Syntax" },
                  ].map(item => (
                    <div key={item.type} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                      <span style={{ width: 11, height: 11, borderRadius: 3, background: item.color, display: "inline-block" }} />
                      <span style={{ color: "#8b949e" }}>{item.label}</span>
                    </div>
                  ))}
                  <span style={{ color: "#484f58", fontSize: 11 }}>· Tap highlighted text to see comment</span>
                </div>
                <div style={{ background: "#0d1117", borderRadius: 10, padding: "20px 22px", border: "1px solid #21262d" }}>
                  <AnnotatedText annotated={result.annotated} />
                </div>
              </div>
            )}

            {/* Tab: Structure Report */}
            {tab === "structure" && (
              <div style={{ background: "#161b22", border: "1px solid #21262d", borderTop: "none", borderRadius: "0 0 12px 12px", padding: "22px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#8b949e", letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 18 }}>
                  Structure Rubric — {WRITING_MODES.find(m => m.value === mode)?.label || mode}
                </div>

                {Object.values(rubric).map(section => (
                  <RubricSection
                    key={section.label}
                    section={section}
                    ratings={result.criteriaRatings}
                    feedback={result.criteriaFeedback}
                  />
                ))}

                {grade >= 6 && result.styleRatings && (
                  <div style={{ border: "1px solid #38343322", borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
                    <div style={{ background: mode === "creative" ? "rgba(167,139,250,0.1)" : "rgba(251,146,60,0.1)", padding: "10px 16px", borderBottom: mode === "creative" ? "1px solid #a78bfa22" : "1px solid #fb923c22", display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{mode === "creative" ? "🎭" : "✒️"}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: mode === "creative" ? "#a78bfa" : "#fb923c" }}>
                        {mode === "creative" ? "Literary Devices (Grades 6–12)" : "Style & Mechanics (Grades 6–12)"}
                      </span>
                    </div>
                    <div style={{ padding: 16 }}>
                      {Object.entries(result.styleRatings).map(([key, val]) => (
                        <RatingBar
                          key={key}
                          label={key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          value={val}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid #21262d" }}>
                  <DownloadReport result={result} grade={grade} mode={mode} text={text} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showTokenModal && (
        <TokenModal
          onClose={() => setShowTokenModal(false)}
          currentBalance={tokenBalance}
        />
      )}

      {/* Footer legal modals */}
      {showFooterTerms   && <TermsModal   readOnly onClose={() => setShowFooterTerms(false)}   onAccept={() => setShowFooterTerms(false)} />}
      {showFooterPrivacy && <PrivacyModal onClose={() => setShowFooterPrivacy(false)} />}
      {showGuide         && <GuideModal   onClose={() => setShowGuide(false)} />}

      {/* Footer */}
      <div style={{
        borderTop: "1px solid #21262d", marginTop: 32,
        padding: "20px 24px", display: "flex",
        alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12,
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Legal links */}
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <button onClick={() => setShowGuide(true)} style={footerLinkStyle}>
            Parent &amp; Student Guide
          </button>
          <span style={{ color: "#30363d" }}>·</span>
          <button onClick={() => setShowFooterTerms(true)} style={footerLinkStyle}>
            Terms of Service
          </button>
          <span style={{ color: "#30363d" }}>·</span>
          <button onClick={() => setShowFooterPrivacy(true)} style={footerLinkStyle}>
            Privacy Policy
          </button>
          <span style={{ color: "#30363d" }}>·</span>
          <span style={{ color: "#484f58", fontSize: 11 }}>
            © {new Date().getFullYear()} Chai Codes
          </span>
        </div>

        {/* Other apps */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: "#484f58", fontSize: 11, marginRight: 4 }}>Also by Chai Codes:</span>
          <a
            href="https://socialskillsexplorer.chaicodes.com"
            target="_blank"
            rel="noopener noreferrer"
            style={footerAppLinkStyle}
          >
            🎯 Social Skills Explorer
          </a>
          <span style={{ color: "#30363d" }}>·</span>
          <a
            href="https://readingcompbuddy.chaicodes.com"
            target="_blank"
            rel="noopener noreferrer"
            style={footerAppLinkStyle}
          >
            📚 Reading Comp Buddy
          </a>
        </div>
      </div>
    </div>
  );
}